import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import './DesignCustomizer.scss';
import CustomizableDesignNotice from '../CustomizableDesignNotice';

// Simple loading spinner component
const LoadingSpinner = () => (
  <div className="loading-spinner-container">
    <div className="loading-spinner" />
  </div>
);

// Product image unavailable component
const ProductImageUnavailable = ({ colorName, colorId }) => {
  // Check if no color is selected or if it's the default
  const isNoColorSelected =
    !colorName || colorName === 'Default' || colorId === 'default';

  if (isNoColorSelected) {
    return (
      <div className="product-image-unavailable">
        <div className="warning-text">
          <span role="img" aria-label="palette">
            🎨
          </span>{' '}
          Choose a product color to see the product preview. You can still
          customize your design if you're not sure what color you'd like.
        </div>
      </div>
    );
  }

  return (
    <div className="product-image-unavailable">
      <div className="warning-text">
        <span role="img" aria-label="camera">
          📸
        </span>{' '}
        We don't have a product photo for this. You can still customize your
        design below.
      </div>
    </div>
  );
};

const DesignCustomizer = ({
  formData,
  handleChange,
  handleBack,
  handleRemoveProduct,
  setModalStepNumber,
  modalStepNumber,
  designGalleryUrl,
}) => {
  const [svgContent, setSvgContent] = useState('');
  const [detectedColors, setDetectedColors] = useState([]);
  const [colorMappings, setColorMappings] = useState(
    (formData.designCustomization &&
      formData.designCustomization.colorMappings) ||
      {}
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [transparentColors, setTransparentColors] = useState(
    new Set(
      (formData.designCustomization &&
        formData.designCustomization.transparentColors) ||
        []
    )
  );
  const [detectedBackgroundColor, setDetectedBackgroundColor] = useState(null);
  const [currentView, setCurrentView] = useState('front');
  const [isViewTransitioning, setIsViewTransitioning] = useState(false);
  const [containerScale, setContainerScale] = useState(1);
  const fetchedDesignsRef = useRef(new Set()); // Track which designs we've already attempted to fetch
  const isFetchingRef = useRef(false); // Track if we're currently fetching to prevent concurrent fetches
  const renderCountRef = useRef(0); // Track render count
  renderCountRef.current += 1;
  const [imageLoadErrors, setImageLoadErrors] = useState(new Set()); // Track images that failed to load
  const [imageLoadingStates, setImageLoadingStates] = useState(new Map()); // Track loading state of images
  const [productImageError, setProductImageError] = useState(false); // Track if product image failed to load
  const [loadedImageUrls, setLoadedImageUrls] = useState(new Set()); // Track which URLs have been loaded
  const [currentImageUrl, setCurrentImageUrl] = useState(null); // Track current displayed image

  // Removed render count logging

  // Component lifecycle - clear refs on unmount (save happens in other effect)
  useEffect(() => {
    return () => {
      // Clear refs on unmount
      fetchedDesignsRef.current.clear();
      isFetchingRef.current = false;
    };
  }, []);

  // Reset product image loading state when product color changes
  useEffect(() => {
    if (formData.productColor && formData.productColor.id) {
      setProductImageError(false);
    }
  }, [formData.productColor]);

  // Also reset loading state when product changes
  useEffect(() => {
    if (formData.product && formData.product.id) {
      setProductImageError(false);
      setLoadedImageUrls(new Set());
    }
  }, [formData.product]);

  // Track which sides have designs - now supports multiple designs per side
  const [sideDesigns, setSideDesigns] = useState(() => {
    // Initialize from saved state

    // ALWAYS load saved designs if they exist, regardless of formData.design
    if (
      formData.designCustomization &&
      formData.designCustomization.sideDesigns
    ) {
      // Found saved sideDesigns
      // Convert old format (single design) to new format (array) if needed
      const designs = formData.designCustomization.sideDesigns;
      const converted = {};
      Object.entries(designs).forEach(([side, design]) => {
        if (design === null) {
          converted[side] = [];
        } else if (Array.isArray(design)) {
          converted[side] = design;
        } else {
          converted[side] = [design];
        }
      });
      // Ensure all sides have arrays
      const result = {
        front: converted.front || [],
        side: converted.side || [],
        back: converted.back || [],
      };
      return result;
    }

    // Otherwise, start with empty sides - designs should go to pool first
    // No saved sideDesigns, starting with empty sides
    return {
      front: [],
      side: [],
      back: [],
    };
  });

  // Design positioning states - now supporting multiple designs per side
  const [designPositions, setDesignPositions] = useState(() => {
    if (
      formData.designCustomization &&
      formData.designCustomization.positions
    ) {
      return formData.designCustomization.positions;
    }
    return {}; // Will be keyed by "side-designId"
  });

  const [designSizes, setDesignSizes] = useState(() => {
    if (formData.designCustomization && formData.designCustomization.sizes) {
      return formData.designCustomization.sizes;
    }
    return {}; // Will be keyed by "side-designId"
  });

  // Track design aspect ratios to properly size bounding boxes
  const [designAspectRatios, setDesignAspectRatios] = useState({});

  // Track SVG content and color mappings per side - now supports multiple designs
  const [sideSvgContent, setSideSvgContent] = useState(() => {
    if (
      formData.designCustomization &&
      formData.designCustomization.sideSvgContent
    ) {
      // Convert old format to new format if needed
      const content = formData.designCustomization.sideSvgContent;
      const converted = {};
      Object.entries(content).forEach(([side, svg]) => {
        if (svg === null) {
          converted[side] = {};
        } else if (typeof svg === 'string') {
          // Old format: single SVG string - convert to object keyed by design ID
          converted[side] = {};
        } else {
          converted[side] = svg;
        }
      });
      return converted;
    }
    return {
      front: {},
      side: {},
      back: {},
    };
  });

  const [sideColorMappings, setSideColorMappings] = useState(() => {
    if (
      formData.designCustomization &&
      formData.designCustomization.sideColorMappings
    ) {
      // Convert old format to new format if needed
      const mappings = formData.designCustomization.sideColorMappings;
      const converted = {};
      Object.entries(mappings).forEach(([side, mapping]) => {
        // Only process valid sides
        if (side === 'front' || side === 'back' || side === 'side') {
          if (!mapping || typeof mapping !== 'object') {
            converted[side] = {};
          } else if (!Object.values(mapping).some(v => typeof v === 'object')) {
            // Old format: flat color mappings - need to be per design
            converted[side] = {};
          } else {
            converted[side] = mapping;
          }
        }
      });

      return converted;
    }
    return {
      front: {},
      side: {},
      back: {},
    };
  });

  // Track which design is currently selected for editing
  const [selectedDesignId, setSelectedDesignId] = useState(null);
  // Track which design is actively being manipulated (for showing bounding box)
  const [activeDesignId, setActiveDesignId] = useState(null);

  // Get current design's position and size
  const getDesignKey = (side, designId) => `${side}-${designId}`;
  const currentDesignKey = selectedDesignId
    ? getDesignKey(currentView, selectedDesignId)
    : null;
  const designPosition =
    currentDesignKey && designPositions[currentDesignKey]
      ? designPositions[currentDesignKey]
      : { x: 50, y: 50 };
  const designSize =
    currentDesignKey && designSizes[currentDesignKey]
      ? designSizes[currentDesignKey]
      : { width: 40, height: 40 };

  const setDesignSize = newSize => {
    if (currentDesignKey) {
      setDesignSizes(prev => ({
        ...prev,
        [currentDesignKey]: newSize,
      }));
    }
  };
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const productContainerRef = useRef(null);
  const productImageRef = useRef(null);
  const [availableColors, setAvailableColors] = useState([]);
  const [isLoadingColors, setIsLoadingColors] = useState(false);
  const [productViews, setProductViews] = useState({});
  const [isLoadingViews, setIsLoadingViews] = useState(false);
  const [removedDesigns, setRemovedDesigns] = useState(new Set()); // Track removed designs
  const [pngDesigns, setPngDesigns] = useState(new Set()); // Track PNG designs that can't be customized
  const processedDesignsRef = useRef(new Set()); // Track all processed design IDs
  const latestStateRef = useRef({}); // Track latest state for unmount save
  const isInitialMount = useRef(true); // Track initial mount to prevent autosave loops
  const lastSavedStateRef = useRef(null); // Track last saved state to prevent duplicate saves

  // Helper to compare if state has actually changed
  const hasStateChanged = newState => {
    if (!lastSavedStateRef.current) return true;

    const oldState = lastSavedStateRef.current;

    // Create a comparison object that excludes frequently changing but non-essential properties
    const getComparableState = state => ({
      sideDesigns: state.sideDesigns,
      designPool: state.designPool,
      positions: state.positions,
      sizes: state.sizes,
      colorMappings: state.colorMappings,
      transparentColors: state.transparentColors,
      pngDesigns: state.pngDesigns,
    });

    const oldComparable = getComparableState(oldState);
    const newComparable = getComparableState(newState);

    return JSON.stringify(oldComparable) !== JSON.stringify(newComparable);
  };

  // Helper to create clean design references without SVG content
  const createCleanDesignReferences = designs => {
    const cleanDesigns = {};
    Object.entries(designs || {}).forEach(([side, sideDesigns]) => {
      if (Array.isArray(sideDesigns)) {
        cleanDesigns[side] = sideDesigns.map(design => ({
          id: design.id,
          artCode: design.artCode,
          url: design.url || design.thumbnailUrl,
          thumbnailUrl: design.thumbnailUrl || design.url,
          svgUrl: design.svgUrl || `/designs/${design.id}/svg_content`,
          // Note: NOT including svgContent
        }));
      } else {
        cleanDesigns[side] = [];
      }
    });
    return cleanDesigns;
  };

  // Removed latestStateRef assignment from here - moved to after all state declarations

  // Design pool for unassigned designs
  const [designPool, setDesignPool] = useState(() => {
    // Check localStorage first for the most recent state
    const localFormData = JSON.parse(localStorage.getItem('formData') || '{}');
    if (
      localFormData.designCustomization &&
      localFormData.designCustomization.designPool
    ) {
      return localFormData.designCustomization.designPool;
    }
    // Fall back to formData if no localStorage
    if (
      formData.designCustomization &&
      formData.designCustomization.designPool
    ) {
      return formData.designCustomization.designPool;
    }
    return [];
  });

  // SVG content for design pool
  const [designPoolSvgContent, setDesignPoolSvgContent] = useState(() => {
    // Check localStorage first for the most recent state
    const localFormData = JSON.parse(localStorage.getItem('formData') || '{}');
    if (
      localFormData.designCustomization &&
      localFormData.designCustomization.designPoolSvgContent
    ) {
      return localFormData.designCustomization.designPoolSvgContent;
    }
    // Fall back to formData if no localStorage
    if (
      formData.designCustomization &&
      formData.designCustomization.designPoolSvgContent
    ) {
      return formData.designCustomization.designPoolSvgContent;
    }
    return {};
  });

  // Update latestStateRef in the render phase - MUST be after all state declarations
  // This avoids triggering re-renders while keeping the latest state available for unmount
  latestStateRef.current = {
    sideDesigns,
    sideColorMappings,
    designPositions,
    designSizes,
    currentView,
    designPool,
    colorMappings,
    transparentColors,
    handleChange,
    createCleanDesignReferences,
    productViews,
    sideSvgContent,
    designPoolSvgContent,
    detectedColors,
    pngDesigns,
  };

  // Auto-save effect - debounced to prevent rapid saves
  useEffect(() => {
    // Skip autosave on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const saveTimeout = setTimeout(() => {
      // Save current state to formData whenever these values change
      if (handleChange) {
        const cleanedDesigns = createCleanDesignReferences(sideDesigns);
        const poolToSave = designPool.map(design => ({
          id: design.id,
          artCode: design.artCode,
          url: design.url || design.thumbnailUrl,
          thumbnailUrl: design.thumbnailUrl || design.url,
          svgUrl: design.svgUrl || `/designs/${design.id}/svg_content`,
        }));

        console.log(
          '[DesignCustomizer] Auto-saving designPool to parent:',
          poolToSave
        );

        const stateToSave = {
          sideDesigns: cleanedDesigns,
          sideColorMappings: sideColorMappings,
          positions: designPositions,
          sizes: designSizes,
          currentView: currentView,
          designPool: poolToSave,
          colorMappings: colorMappings,
          transparentColors: Array.from(transparentColors),
          position: designPositions.front || { x: 50, y: 50 },
          size: designSizes.front || { width: 40, height: 40 },
          // Include product views and SVG content
          productViews: productViews,
          sideSvgContent: sideSvgContent,
          designPoolSvgContent: designPoolSvgContent,
          inkColors: detectedColors.map(color => ({
            id: color.id || color.name,
            name: color.name,
            hex_code: color.hex_code || color.hexCode,
          })),
          // Include PNG designs set
          pngDesigns: Array.from(pngDesigns),
        };

        // Only save if state has actually changed
        if (hasStateChanged(stateToSave)) {
          lastSavedStateRef.current = stateToSave;
          handleChange('designCustomization', stateToSave);
        }
      }
    }, 1000);

    return () => clearTimeout(saveTimeout);
  }, [
    sideDesigns,
    sideColorMappings,
    designPositions,
    designSizes,
    currentView,
    designPool,
    colorMappings,
    transparentColors,
    handleChange,
  ]);

  // Sync designPool when formData changes (e.g., when modal reopens)
  // Use JSON.stringify as dependency to avoid triggering on object reference changes
  const formDataPoolSignature = useMemo(() => {
    if (
      formData.designCustomization &&
      formData.designCustomization.designPool
    ) {
      return formData.designCustomization.designPool
        .map(d => String(d.id))
        .sort()
        .join(',');
    }
    return '';
  }, [formData.designCustomization]);

  const formDataSvgSignature = useMemo(() => {
    if (
      formData.designCustomization &&
      formData.designCustomization.designPoolSvgContent
    ) {
      return Object.keys(formData.designCustomization.designPoolSvgContent)
        .sort()
        .join(',');
    }
    return '';
  }, [formData.designCustomization]);

  useEffect(() => {
    if (
      formData.designCustomization &&
      formData.designCustomization.designPool
    ) {
      const currentPoolIds = designPool
        .map(d => String(d.id))
        .sort()
        .join(',');

      // Only update if the pools are actually different
      if (currentPoolIds !== formDataPoolSignature && formDataPoolSignature) {
        console.log(
          '[DesignCustomizer] Syncing designPool from formData prop:',
          formData.designCustomization.designPool
        );
        setDesignPool(formData.designCustomization.designPool);
      }
    }
  }, [formDataPoolSignature]);

  useEffect(() => {
    if (
      formData.designCustomization &&
      formData.designCustomization.designPoolSvgContent
    ) {
      const currentSvgKeys = Object.keys(designPoolSvgContent)
        .sort()
        .join(',');

      // Only update if the SVG content is actually different
      if (currentSvgKeys !== formDataSvgSignature && formDataSvgSignature) {
        setDesignPoolSvgContent(
          formData.designCustomization.designPoolSvgContent
        );
      }
    }
  }, [formDataSvgSignature]);

  // Listen for designs being added from the gallery while modal is open
  useEffect(() => {
    const handleLoadDesignFromGallery = () => {
      // Reload formData from localStorage to get the latest design pool
      const latestFormData = JSON.parse(
        localStorage.getItem('formData') || '{}'
      );

      if (
        latestFormData.designCustomization &&
        latestFormData.designCustomization.designPool
      ) {
        const latestPool = latestFormData.designCustomization.designPool;
        const currentPoolIds = designPool
          .map(d => String(d.id))
          .sort()
          .join(',');
        const latestPoolIds = latestPool
          .map(d => String(d.id))
          .sort()
          .join(',');

        // Only update if the pools are different
        if (currentPoolIds !== latestPoolIds) {
          console.log('Design added from gallery, updating pool:', latestPool);
          setDesignPool(latestPool);
        }
      }
    };

    const handleStorageEvent = () => {
      // Also listen for storage events (triggered by DesignGalleryIntegration)
      handleLoadDesignFromGallery();
    };

    window.addEventListener(
      'LoadDesignFromGallery',
      handleLoadDesignFromGallery
    );
    window.addEventListener('storage', handleStorageEvent);

    return () => {
      window.removeEventListener(
        'LoadDesignFromGallery',
        handleLoadDesignFromGallery
      );
      window.removeEventListener('storage', handleStorageEvent);
    };
  }, [designPool]);

  // Standard ink colors - you can update these based on your actual ink colors
  const standardInkColors = useMemo(
    () => [
      // Reds
      { name: 'Red', hex: '#EB0028' },
      { name: 'Cardinal Red', hex: '#A02741' },
      { name: 'Maroon', hex: '#73243C' },
      { name: 'Watermelon', hex: '#F15A5F' },
      { name: 'Coral', hex: '#FF8671' },

      // Oranges
      { name: 'Orange', hex: '#FF5000' },
      { name: 'Tiger Orange', hex: '#F58220' },
      { name: 'Melon', hex: '#FAAE61' },
      { name: 'Light Peach', hex: '#EBC2B2' },

      // Yellows
      { name: 'Yellow', hex: '#FCD732' },
      { name: 'Yellow Gold', hex: '#FFB718' },
      { name: 'Old Gold', hex: '#AF841D' },
      { name: 'Pale Yellow', hex: '#EDE499' },

      // Greens
      { name: 'Fluo Green', hex: '#00D24B' },
      { name: 'Bright Green', hex: '#82BC00' },
      { name: 'Lime Green', hex: '#009A39' },
      { name: 'Kelly Green', hex: '#006649' },
      { name: 'Dark Green', hex: '#00514B' },
      { name: 'Mint Green', hex: '#A0D8B3' },
      { name: 'Olive Green', hex: '#85B09A' },

      // Blues
      { name: 'Bright Blue', hex: '#00A8DA' },
      { name: 'Columbia Blue', hex: '#3E8DDD' },
      { name: 'Powder Blue', hex: '#ADC8E7' },
      { name: 'Royal Blue', hex: '#003CA6' },
      { name: 'Marine Blue', hex: '#006CA7' },
      { name: 'Navy Blue', hex: '#002855' },
      { name: 'Teal', hex: '#00BFD5' },
      { name: 'Aqua', hex: '#008995' },

      // Purples/Violets
      { name: 'Purple', hex: '#62259D' },
      { name: 'Grape', hex: '#8465AC' },
      { name: 'Lilac', hex: '#BC83C9' },

      // Pinks
      { name: 'Hot Pink', hex: '#EA1C75' },
      { name: 'Light Pink', hex: '#FDA3BA' },

      // Browns
      { name: 'Dark Brown', hex: '#513528' },
      { name: 'Chocolate', hex: '#9E662E' },
      { name: 'Light Brown', hex: '#89421C' },
      { name: 'Khaki', hex: '#DFC9A2' },

      // Grays/Neutrals
      { name: 'Black', hex: '#000000' },
      { name: 'Dark Grey', hex: '#56585A' },
      { name: 'Medium Grey', hex: '#7C878E' },
      { name: 'Light Grey', hex: '#A3AAAE' },
      { name: 'Cream', hex: '#EBE3D7' },
      { name: 'Basic White', hex: '#F9F9F9' },
    ],
    []
  );

  // Create maps for quick color lookups - O(1) instead of O(n)
  const hexToColorName = useMemo(() => {
    const map = {};
    standardInkColors.forEach(color => {
      map[color.hex.toUpperCase()] = color.name;
    });
    return map;
  }, [standardInkColors]);

  const colorByHexMap = useMemo(() => {
    const map = new Map();
    standardInkColors.forEach(color => {
      map.set(color.hex.toLowerCase(), color);
      map.set(color.hex.toUpperCase(), color);
    });
    return map;
  }, [standardInkColors]);

  // Function to get color name or hex
  const getColorDisplay = hex => {
    // Normalize the hex color
    let normalizedHex = hex.toUpperCase();

    // Handle 3-digit hex codes by expanding them
    if (normalizedHex.match(/^#[0-9A-F]{3}$/)) {
      normalizedHex = `#${normalizedHex[1]}${normalizedHex[1]}${
        normalizedHex[2]
      }${normalizedHex[2]}${normalizedHex[3]}${normalizedHex[3]}`;
    }

    return hexToColorName[normalizedHex] || hex;
  };

  // Update localStorage with current designs
  const updateLocalStorageDesigns = designs => {
    const designsArray = [];
    const seenIds = new Set();

    Object.entries(designs).forEach(([side, sideDesigns]) => {
      if (Array.isArray(sideDesigns)) {
        sideDesigns.forEach(design => {
          if (design && design.id && !seenIds.has(String(design.id))) {
            seenIds.add(String(design.id));
            designsArray.push({
              id: design.id,
              artCode: design.artCode,
              url: design.url,
              thumbnailUrl: design.thumbnailUrl,
              svgUrl: design.svgUrl || `/designs/${design.id}/svg_content`,
              // Note: NOT storing svgContent to avoid localStorage quota issues
            });
          }
        });
      }
    });

    try {
      if (designsArray.length > 0) {
        localStorage.setItem('selectedDesigns', JSON.stringify(designsArray));
      } else {
        localStorage.removeItem('selectedDesigns');
      }
    } catch (e) {
      if (e.name === 'QuotaExceededError') {
        alert('Storage quota exceeded. Some designs may not be saved.');
      }
    }
  };

  // Clean up localStorage on unmount - this is the ONLY unmount effect that saves state
  useEffect(() => {
    return () => {
      // Save state one final time before unmounting using the latest ref
      if (handleChange && latestStateRef.current) {
        const state = latestStateRef.current;
        console.log(
          '[DesignCustomizer] UNMOUNT - saving final state, designPool:',
          state.designPool
        );
        const finalState = {
          sideDesigns: createCleanDesignReferences(state.sideDesigns),
          sideColorMappings: state.sideColorMappings,
          positions: state.designPositions,
          sizes: state.designSizes,
          currentView: state.currentView,
          designPool: (state.designPool || []).map(design => ({
            id: design.id,
            artCode: design.artCode,
            url: design.url || design.thumbnailUrl,
            thumbnailUrl: design.thumbnailUrl || design.url,
            svgUrl: design.svgUrl || `/designs/${design.id}/svg_content`,
          })),
          colorMappings: state.colorMappings,
          transparentColors: Array.from(state.transparentColors),
          position: state.designPositions.front || { x: 50, y: 50 },
          size: state.designSizes.front || { width: 40, height: 40 },
          // Include product views and SVG content
          productViews: state.productViews,
          sideSvgContent: state.sideSvgContent,
          designPoolSvgContent: state.designPoolSvgContent,
          inkColors:
            state.detectedColors &&
            state.detectedColors.map(color => ({
              id: color.id || color.name,
              name: color.name,
              hex_code: color.hex_code || color.hexCode,
            })),
        };
        handleChange('designCustomization', finalState);
      }

      // Clean up temporary localStorage items on unmount
      localStorage.removeItem('targetDesignSide');
      localStorage.removeItem('addingMoreDesigns');
      localStorage.removeItem('modalStepNumber');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Remove handleChange dependency to prevent constant unmounting

  // Fetch SVG content for any designs that don't have it
  useEffect(() => {
    Object.entries(sideDesigns).forEach(([side, designs]) => {
      designs.forEach(design => {
        const hasSvg = sideSvgContent[side] && sideSvgContent[side][design.id];
        if (!hasSvg && design.id) {
          fetchAndParseSVG(design, side);
        }
      });
    });
  }, [sideDesigns, sideSvgContent, fetchAndParseSVG]); // Proper dependencies

  // Don't sync design pool from formData after initial mount
  // This prevents stale data from overriding user changes

  useEffect(() => {
    // First, handle legacy formData.design by converting it to sessionStorage format
    if (formData.design && formData.design.id) {
      // Check if we've already processed this specific design
      if (processedDesignsRef.current.has(String(formData.design.id))) {
        // Design already processed, skipping
        return;
      }

      // Check if this design is already in the pool
      const alreadyInPool = designPool.some(
        d => String(d.id) === String(formData.design.id)
      );
      if (alreadyInPool) {
        return;
      }

      // Check if this design has been explicitly removed
      if (removedDesigns.has(String(formData.design.id))) {
        return;
      }

      // Mark this specific design as processed
      processedDesignsRef.current.add(String(formData.design.id));

      // Always treat formData.design as a new design that should go to pool

      // This is a new design - add it to the pool
      const newDesign = {
        id: formData.design.id,
        url: formData.design.url,
        thumbnailUrl: formData.design.url,
        artCode: formData.design.artCode,
        designUrl: formData.design.designUrl,
        svgUrl: `/designs/${formData.design.id}/svg_content`,
      };

      // Add to design pool immediately using functional update to ensure we have latest state
      setDesignPool(prev => {
        // Check if design already exists in pool
        const exists = prev.some(d => String(d.id) === String(newDesign.id));
        if (exists) {
          return prev;
        }
        return [...prev, newDesign];
      });

      // Remove from removedDesigns set if it was there
      setRemovedDesigns(prev => {
        const newSet = new Set(prev);
        newSet.delete(String(newDesign.id));
        return newSet;
      });

      // Fetch SVG content for the new design
      fetchAndParseSVG(formData.design, null, true); // true = for pool

      // Don't remove the design from sides - it should stay where it was assigned
      // The user can manually remove it if needed

      // Save the state immediately without clearing the design
      handleChange('designCustomization', {
        ...formData.designCustomization,
        sideDesigns: sideDesigns,
        designPool: [
          ...((formData.designCustomization &&
            formData.designCustomization.designPool) ||
            []),
          newDesign,
        ].filter(
          (design, index, self) =>
            index === self.findIndex(d => String(d.id) === String(design.id))
        ),
        sideColorMappings: sideColorMappings,
        positions: designPositions,
        sizes: designSizes,
        currentView: currentView,
      });

      // Don't process sessionStorage if we handled formData.design
      return;
    }

    // Check for selected designs from gallery
    try {
      // Check if we're returning from adding more designs
      const isAddingMore = localStorage.getItem('addingMoreDesigns') === 'true';

      // Only process designs if we're actually returning from the gallery
      if (!isAddingMore) {
        // Not returning from gallery, skip processing
        return;
      }

      localStorage.removeItem('addingMoreDesigns'); // Clean up flag

      // First check for existing designs that should be preserved
      const existingDesignsStr = localStorage.getItem('existingDesigns');
      let existingDesigns = [];
      if (existingDesignsStr) {
        existingDesigns = JSON.parse(existingDesignsStr);
        // Found existing designs
        localStorage.removeItem('existingDesigns'); // Clean up
      }

      // Then check for new designs from gallery
      let newDesignsStr = sessionStorage.getItem('selectedDesigns');
      if (!newDesignsStr) {
        // Check localStorage
        newDesignsStr = localStorage.getItem('selectedDesigns');
      }

      // If we have existing designs but no new ones, just restore the existing ones
      if (existingDesigns.length > 0 && !newDesignsStr) {
        // Restoring existing designs only
        const newSideDesigns = { front: [], side: [], back: [] };
        const newSideSvgContent = { front: {}, side: {}, back: {} };
        const newDesignPool = [];
        const newDesignPoolSvgContent = {};

        existingDesigns.forEach(design => {
          if (design.assignedSide && design.assignedSide !== 'pool') {
            // Design has an assigned side, put it there
            const side = design.assignedSide;
            newSideDesigns[side].push({
              id: design.id,
              artCode: design.artCode,
              url: design.url || design.thumbnailUrl,
              thumbnailUrl: design.thumbnailUrl || design.url,
              svgContent: design.svgContent,
              svgUrl: design.svgUrl || `/designs/${design.id}/svg_content`,
            });
            if (design.svgContent) {
              // Check for rasterized content
              if (hasRasterizedContent(design.svgContent)) {
                setPngDesigns(prev => new Set([...prev, String(design.id)]));
              }

              const cleanedSvg = design.svgContent
                .replace(/\[@tags=\[\]\]>/g, '')
                .replace(/\[>/g, '')
                .replace(/\]>/g, '')
                .replace(/<image[^>]*xlink:href="[^"]*"[^>]*>/g, '')
                .replace(/href="http[^"]*"/g, 'href="#"');
              newSideSvgContent[side][design.id] = cleanedSvg;
            }
          } else {
            // No assigned side or marked as 'pool', put in design pool
            newDesignPool.push({
              id: design.id,
              artCode: design.artCode,
              url: design.url || design.thumbnailUrl,
              thumbnailUrl: design.thumbnailUrl || design.url,
              svgContent: design.svgContent,
              svgUrl: design.svgUrl || `/designs/${design.id}/svg_content`,
            });
            if (design.svgContent) {
              // Check for rasterized content
              if (hasRasterizedContent(design.svgContent)) {
                setPngDesigns(prev => new Set([...prev, String(design.id)]));
              }

              const cleanedSvg = design.svgContent
                .replace(/\[@tags=\[\]\]>/g, '')
                .replace(/\[>/g, '')
                .replace(/\]>/g, '')
                .replace(/<image[^>]*xlink:href="[^"]*"[^>]*>/g, '')
                .replace(/href="http[^"]*"/g, 'href="#"');
              newDesignPoolSvgContent[design.id] = cleanedSvg;
            }
          }
        });

        setSideDesigns(newSideDesigns);
        setSideSvgContent(newSideSvgContent);
        setDesignPool(newDesignPool);
        setDesignPoolSvgContent(newDesignPoolSvgContent);
        setIsLoading(false);
        return;
      }

      if (newDesignsStr) {
        const newDesigns = JSON.parse(newDesignsStr);

        // Create a combined list without duplicates
        const designMap = new Map();

        // Add existing designs first (they have priority)
        existingDesigns.forEach(design => {
          designMap.set(design.id, design);
        });

        // Add new designs (won't overwrite existing)
        newDesigns.forEach(design => {
          if (!designMap.has(design.id)) {
            designMap.set(design.id, design);
          }
        });

        const uniqueDesigns = Array.from(designMap.values()).filter(
          design => !removedDesigns.has(String(design.id))
        );

        if (uniqueDesigns.length > 0) {
          // Initialize side designs with the selected designs
          const newSideDesigns = { front: [], side: [], back: [] };
          const newSideSvgContent = { front: {}, side: {}, back: {} };
          const newDesignPool = [];
          const newDesignPoolSvgContent = {};

          // Find the first available slot for each design
          const sides = ['front', 'back', 'side'];

          // First, restore designs to their original sides
          const designsToFetch = [];

          uniqueDesigns.forEach(design => {
            // If this design has an assigned side (from existing designs), use it
            if (design.assignedSide && design.assignedSide !== 'pool') {
              const side = design.assignedSide;
              newSideDesigns[side].push({
                id: design.id,
                artCode: design.artCode,
                url: design.url || design.thumbnailUrl,
                thumbnailUrl: design.thumbnailUrl || design.url,
                svgContent: design.svgContent,
                svgUrl: design.svgUrl,
              });
              if (design.svgContent) {
                // Check for rasterized content
                if (hasRasterizedContent(design.svgContent)) {
                  setPngDesigns(prev => new Set([...prev, String(design.id)]));
                }

                const cleanedSvg = design.svgContent
                  .replace(/\[@tags=\[\]\]>/g, '')
                  .replace(/\[>/g, '')
                  .replace(/\]>/g, '')
                  .replace(/<image[^>]*xlink:href="[^"]*"[^>]*>/g, '')
                  .replace(/href="http[^"]*"/g, 'href="#"');
                newSideSvgContent[side][design.id] = cleanedSvg;
              } else if (design.svgUrl) {
                // Need to fetch SVG content
                designsToFetch.push({ design, side });
              }
            } else if (design.assignedSide === 'pool') {
              // This design should go to the pool
              newDesignPool.push({
                id: design.id,
                artCode: design.artCode,
                url: design.url || design.thumbnailUrl,
                thumbnailUrl: design.thumbnailUrl || design.url,
                svgContent: design.svgContent,
                svgUrl: design.svgUrl || `/designs/${design.id}/svg_content`,
              });
              if (design.svgContent) {
                // Check for rasterized content
                if (hasRasterizedContent(design.svgContent)) {
                  setPngDesigns(prev => new Set([...prev, String(design.id)]));
                }

                const cleanedSvg = design.svgContent
                  .replace(/\[@tags=\[\]\]>/g, '')
                  .replace(/\[>/g, '')
                  .replace(/\]>/g, '')
                  .replace(/<image[^>]*xlink:href="[^"]*"[^>]*>/g, '')
                  .replace(/href="http[^"]*"/g, 'href="#"');
                newDesignPoolSvgContent[design.id] = cleanedSvg;
              } else if (design.svgUrl) {
                // Need to fetch SVG content for pool
                designsToFetch.push({ design, pool: true });
              }
            }
          });

          // Check if we have a target side for new designs
          const targetSide = localStorage.getItem('targetDesignSide');

          // Add new designs (without assignedSide) to the design pool
          uniqueDesigns.forEach(design => {
            // Skip if this design already has an assigned side
            if (design.assignedSide) {
              return;
            }

            // Add to design pool instead of auto-assigning
            newDesignPool.push({
              id: design.id,
              artCode: design.artCode,
              url: design.url || design.thumbnailUrl,
              thumbnailUrl: design.thumbnailUrl || design.url,
              svgContent: design.svgContent,
              svgUrl: design.svgUrl || `/designs/${design.id}/svg_content`,
            });

            if (design.svgContent) {
              // Check for rasterized content
              if (hasRasterizedContent(design.svgContent)) {
                setPngDesigns(prev => new Set([...prev, String(design.id)]));
              }

              const cleanedSvg = design.svgContent
                .replace(/\[@tags=\[\]\]>/g, '')
                .replace(/\[>/g, '')
                .replace(/\]>/g, '')
                .replace(/<image[^>]*xlink:href="[^"]*"[^>]*>/g, '')
                .replace(/href="http[^"]*"/g, 'href="#"');
              newDesignPoolSvgContent[design.id] = cleanedSvg;
            } else if (design.svgUrl) {
              // Need to fetch SVG content for pool
              designsToFetch.push({ design, pool: true });
            }
          });

          setSideDesigns(newSideDesigns);
          setSideSvgContent(newSideSvgContent);
          setDesignPool(newDesignPool);
          setDesignPoolSvgContent(newDesignPoolSvgContent);

          // Fetch SVG content for designs that don't have it
          if (designsToFetch.length > 0) {
            Promise.all(
              designsToFetch.map(async ({ design, side, pool }) => {
                try {
                  const response = await fetch(design.svgUrl);
                  const data = await response.json();
                  if (data.success && data.svg_content) {
                    // Check for rasterized content
                    if (hasRasterizedContent(data.svg_content)) {
                      setPngDesigns(
                        prev => new Set([...prev, String(design.id)])
                      );
                    }

                    const cleanedSvg = data.svg_content
                      .replace(/\[@tags=\[\]\]>/g, '')
                      .replace(/\[>/g, '')
                      .replace(/\]>/g, '')
                      .replace(/<image[^>]*xlink:href="[^"]*"[^>]*>/g, '')
                      .replace(/href="http[^"]*"/g, 'href="#"');

                    // Calculate aspect ratio from SVG
                    const aspectRatio = getAspectRatioFromSVG(cleanedSvg);
                    setDesignAspectRatios(prev => ({
                      ...prev,
                      [design.id]: aspectRatio,
                    }));

                    if (pool) {
                      // Update the SVG content for design pool
                      setDesignPoolSvgContent(prev => ({
                        ...prev,
                        [design.id]: cleanedSvg,
                      }));
                    } else {
                      // Update the SVG content for this side
                      setSideSvgContent(prev => ({
                        ...prev,
                        [side]: {
                          ...prev[side],
                          [design.id]: cleanedSvg,
                        },
                      }));
                    }

                    // If this is the current view, update the main SVG
                    if (
                      side === currentView ||
                      (!svgContent && side === 'front')
                    ) {
                      setSvgContent(cleanedSvg);
                      const colors = extractColorsFromSVG(cleanedSvg);
                      setDetectedColors(colors);
                      const bgColor = detectBackgroundColor(cleanedSvg);
                      setDetectedBackgroundColor(bgColor);

                      // Initialize color mappings
                      const initialMappings = {};
                      colors.forEach(color => {
                        initialMappings[color] = color;
                      });
                      setColorMappings(initialMappings);
                    }
                  }
                } catch (e) {}
              })
            );
          }

          // Don't automatically select any design - let users place them manually
          // Keep selectedDesignId as null until user manually selects a design

          // Clear both session and localStorage after successful processing
          sessionStorage.removeItem('selectedDesigns');
          localStorage.removeItem('selectedDesigns');
          // Also clear targetDesignSide if it exists
          localStorage.removeItem('targetDesignSide');
          setIsLoading(false);
          return;
        }
      }
    } catch (e) {
      // Error processing selected designs
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.design && formData.design.id, designPool]); // Re-run when design ID changes or pool updates

  // Track the design IDs we've loaded to prevent re-loading on position changes
  const loadedDesignIdsRef = useRef(new Set());

  // Load designs from designCustomization if they exist
  useEffect(() => {
    if (
      formData.designCustomization &&
      formData.designCustomization.sideDesigns
    ) {
      const { sideDesigns: savedSideDesigns } = formData.designCustomization;
      let needsLoading = false;

      // Check each side for designs that need SVG content loaded
      Object.entries(savedSideDesigns).forEach(([side, designs]) => {
        if (Array.isArray(designs)) {
          designs.forEach(design => {
            if (
              design &&
              design.id &&
              !loadedDesignIdsRef.current.has(`${side}-${design.id}`) &&
              (!sideSvgContent[side] || !sideSvgContent[side][design.id])
            ) {
              needsLoading = true;
              loadedDesignIdsRef.current.add(`${side}-${design.id}`);
              fetchAndParseSVG(design, side);
            }
          });
        } else if (designs && designs.id) {
          // Handle old format (single design)
          if (
            !loadedDesignIdsRef.current.has(`${side}-${designs.id}`) &&
            (!sideSvgContent[side] || !sideSvgContent[side][designs.id])
          ) {
            needsLoading = true;
            loadedDesignIdsRef.current.add(`${side}-${designs.id}`);
            fetchAndParseSVG(designs, side);
          }
        }
      });

      // Only log if we actually loaded something
      if (!needsLoading) {
        console.log(
          '[DesignCustomizer] loadDesignCustomization - no new designs to load, skipping'
        );
      }
    }
  }, [formData.designCustomization, sideSvgContent, fetchAndParseSVG]); // Keep proper dependencies

  // Store product ID separately to avoid complex dependency
  const productId = formData.product && formData.product.id;

  // Initialize colors from saved data if available
  useEffect(() => {
    if (
      formData.productColor &&
      formData.productColor.id !== 'default' &&
      availableColors.length === 0
    ) {
      setAvailableColors([formData.productColor]);
    }
  }, []); // Only run once on mount

  // Fetch available colors when product is loaded
  useEffect(() => {
    if (productId) {
      // Always fetch colors when product changes to ensure we have the latest
      fetchProductColors();

      // Also fetch S&S product views if we have a style
      if (formData.product && formData.product.style) {
        fetchProductViews();
      }
    } else {
      // Clear colors when no product is selected
      setAvailableColors([]);
      setProductViews({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]); // Only re-run if product ID changes - functions are memoized with useCallback

  // Auto-save position changes - but not while dragging or resizing
  useEffect(() => {
    if (svgContent && !isLoading && !isDragging && !isResizing) {
      saveCustomizationState(null, null, designPositions, designSizes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designPositions, designSizes, isDragging, isResizing]); // Don't include saveCustomizationState to avoid loops

  // Calculate container scale to maintain consistent design sizes
  useEffect(() => {
    const calculateScale = () => {
      if (!productContainerRef.current) return;

      // Base width for desktop view (450px from CSS)
      const baseWidth = 450;
      const currentWidth = productContainerRef.current.offsetWidth;

      // Calculate scale factor
      const scale = currentWidth / baseWidth;
      setContainerScale(scale);
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);

    return () => window.removeEventListener('resize', calculateScale);
  }, [currentView]); // Recalculate when view changes

  // Update colors when switching views or selecting different design
  useEffect(() => {
    const currentDesigns = sideDesigns[currentView] || [];

    if (currentDesigns.length > 0) {
      // Check if selectedDesignId exists on current side
      if (
        !selectedDesignId ||
        !currentDesigns.find(d => String(d.id) === String(selectedDesignId))
      ) {
        // Auto-select the first design on this side to keep the UI consistent
        const firstDesign = currentDesigns[0];
        setSelectedDesignId(firstDesign.id);

        // Load the first design's SVG if available
        if (
          sideSvgContent[currentView] &&
          sideSvgContent[currentView][firstDesign.id]
        ) {
          const svgText = sideSvgContent[currentView][firstDesign.id];
          setSvgContent(svgText);
          const colors = extractColorsFromSVG(svgText);
          setDetectedColors(colors);

          const existingMappings =
            (sideColorMappings[currentView] &&
              sideColorMappings[currentView][firstDesign.id]) ||
            {};

          const initialMappings = {};
          const transparents = new Set();
          colors.forEach(color => {
            initialMappings[color] = existingMappings[color] || color;
            if (existingMappings[color] === 'transparent') {
              transparents.add(color);
            }
          });

          setColorMappings(initialMappings);
          setTransparentColors(transparents);

          // Check background
          const bgColor = detectBackgroundColor(svgText);
          if (bgColor && transparents.has(bgColor)) {
            setDetectedBackgroundColor(bgColor);
          }
        } else if (
          !sideSvgContent[currentView] ||
          !sideSvgContent[currentView][firstDesign.id]
        ) {
          // Need to fetch SVG content for first design
          fetchAndParseSVG(firstDesign, currentView);
        }
        return;
      }

      const currentDesign = currentDesigns.find(
        d => String(d.id) === String(selectedDesignId)
      );
      if (
        currentDesign &&
        sideSvgContent[currentView] &&
        sideSvgContent[currentView][selectedDesignId]
      ) {
        // Load SVG content for selected design
        const svgText = sideSvgContent[currentView][selectedDesignId];
        setSvgContent(svgText);

        // Extract colors from the SVG
        const colors = extractColorsFromSVG(svgText);
        setDetectedColors(colors);

        // Load existing color mappings for this design or initialize them
        const existingMappings =
          (sideColorMappings[currentView] &&
            sideColorMappings[currentView][selectedDesignId]) ||
          {};

        const initialMappings = {};
        const transparents = new Set();
        colors.forEach(color => {
          initialMappings[color] = existingMappings[color] || color;
          // Check if this color is mapped to transparent
          if (existingMappings[color] === 'transparent') {
            transparents.add(color);
          }
        });
        setColorMappings(initialMappings);

        // Always detect background color for proper styling
        const bgColor = detectBackgroundColor(svgText);
        setDetectedBackgroundColor(bgColor);

        // Update transparent colors based on saved mappings
        if (transparents.size > 0) {
          setTransparentColors(transparents);
          // Check if any transparent color is the background
          if (bgColor && transparents.has(bgColor)) {
          }
        } else {
          setTransparentColors(new Set());
        }
      } else if (
        currentDesign &&
        (!sideSvgContent[currentView] ||
          !sideSvgContent[currentView][selectedDesignId])
      ) {
        // Need to fetch SVG content for this design on this side
        fetchAndParseSVG(currentDesign, currentView);
      }
    } else {
      // No designs on this side, clear the view
      setSvgContent('');
      setDetectedColors([]);
      setColorMappings({});
      setSelectedDesignId(null);
      setDetectedBackgroundColor(null);
      setTransparentColors(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentView,
    selectedDesignId,
    sideDesigns,
    sideSvgContent,
    sideColorMappings,
    // Note: extractColorsFromSVG, detectBackgroundColor, and fetchAndParseSVG
    // are used in this effect but are wrapped in useCallback with stable references.
    // They are defined later in the file, so including them would cause
    // "used before defined" errors. The eslint-disable above acknowledges this.
  ]);

  // Load SVG content for saved designs that don't have it
  useEffect(() => {
    // Prevent running if we're already fetching
    if (isFetchingRef.current) {
      return;
    }

    const fetchMissingSvgContent = async () => {
      const designsToFetch = [];

      // Check each side for designs without SVG content
      Object.entries(sideDesigns).forEach(([side, designs]) => {
        if (Array.isArray(designs)) {
          designs.forEach(design => {
            const fetchKey = `${side}-${design.id}`;
            if (
              design &&
              design.id &&
              (!sideSvgContent[side] || !sideSvgContent[side][design.id]) &&
              !fetchedDesignsRef.current.has(fetchKey) // Only fetch if we haven't tried before
            ) {
              designsToFetch.push({ side, design });
              fetchedDesignsRef.current.add(fetchKey); // Mark as attempted
            }
          });
        }
      });

      if (designsToFetch.length > 0) {
        // Fetching SVG content for saved designs
        isFetchingRef.current = true; // Mark as fetching
        setIsLoading(true);

        try {
          for (const { side, design } of designsToFetch) {
            try {
              const svgUrl =
                design.svgUrl || `/designs/${design.id}/svg_content`;
              // Fetching SVG for design
              const response = await fetch(svgUrl);
              // SVG fetch response
              const data = await response.json();

              if (data.success && data.svg_content) {
                // Check for rasterized content
                if (hasRasterizedContent(data.svg_content)) {
                  setPngDesigns(prev => new Set([...prev, String(design.id)]));
                }

                const cleanedSvg = data.svg_content
                  .replace(/\[@tags=\[\]\]>/g, '')
                  .replace(/\[>/g, '')
                  .replace(/\]>/g, '')
                  .replace(/<image[^>]*xlink:href="[^"]*"[^>]*>/g, '')
                  .replace(/href="http[^"]*"/g, 'href="#"');

                // Calculate aspect ratio
                const aspectRatio = getAspectRatioFromSVG(cleanedSvg);
                setDesignAspectRatios(prev => ({
                  ...prev,
                  [design.id]: aspectRatio,
                }));

                setSideSvgContent(prev => ({
                  ...prev,
                  [side]: {
                    ...prev[side],
                    [design.id]: cleanedSvg,
                  },
                }));

                // Don't update main SVG content here - let the view change effect handle it
                // This prevents triggering other effects unnecessarily
                // Successfully loaded SVG
              } else {
                // No SVG content returned
              }
            } catch (e) {}
          }
        } finally {
          // Always clear the loading state when done
          // Finished fetching SVGs
          isFetchingRef.current = false; // Clear fetching flag
          setIsLoading(false);
        }
      } else {
        // No designs need SVG content fetched, all cached
      }
    };

    // Only run if we're not already fetching and we have saved designs
    if (
      !isFetchingRef.current &&
      Object.values(sideDesigns).some(
        designs => Array.isArray(designs) && designs.length > 0
      )
    ) {
      fetchMissingSvgContent();
    }
  }, [sideDesigns]); // Only depend on sideDesigns to avoid infinite loops

  // Fetch missing SVG content for design pool
  useEffect(() => {
    const fetchMissingPoolSvgContent = async () => {
      const designsToFetch = [];

      // Check design pool for designs without SVG content
      designPool.forEach(design => {
        const fetchKey = `pool-${design.id}`;
        if (
          design &&
          design.id &&
          !designPoolSvgContent[design.id] &&
          !fetchedDesignsRef.current.has(fetchKey) // Only fetch if we haven't tried before
        ) {
          designsToFetch.push(design);
          fetchedDesignsRef.current.add(fetchKey); // Mark as attempted
        }
      });

      if (designsToFetch.length > 0) {
        for (const design of designsToFetch) {
          try {
            const svgUrl = design.svgUrl || `/designs/${design.id}/svg_content`;
            const response = await fetch(svgUrl);
            const data = await response.json();

            if (data.success && data.svg_content) {
              // Check for rasterized content
              if (hasRasterizedContent(data.svg_content)) {
                setPngDesigns(prev => new Set([...prev, String(design.id)]));
              }

              const cleanedSvg = data.svg_content
                .replace(/\[@tags=\[\]\]>/g, '')
                .replace(/\[>/g, '')
                .replace(/\]>/g, '')
                .replace(/<image[^>]*xlink:href="[^"]*"[^>]*>/g, '')
                .replace(/href="http[^"]*"/g, 'href="#"');

              setDesignPoolSvgContent(prev => ({
                ...prev,
                [design.id]: cleanedSvg,
              }));
            }
          } catch (e) {}
        }
      }
    };

    // Only run if we have pool designs
    if (designPool.length > 0) {
      fetchMissingPoolSvgContent();
    }
  }, [designPool]); // Don't include designPoolSvgContent to avoid infinite loop

  // Handle currentView changes - update selected design and load its colors
  useEffect(() => {
    const designs = sideDesigns[currentView] || [];

    // Don't automatically select any design - let users manually select them
    // Only keep a design selected if it exists on the current side

    // If the selected design exists on this side, load its colors
    if (
      selectedDesignId &&
      designs.some(d => String(d.id) === String(selectedDesignId))
    ) {
      const svgContent =
        sideSvgContent[currentView] &&
        sideSvgContent[currentView][selectedDesignId];
      if (svgContent) {
        setSvgContent(svgContent);

        // Extract colors from this design's SVG
        const colors = extractColorsFromSVG(svgContent);
        setDetectedColors(colors);

        // Always detect background color for proper styling
        const bgColor = detectBackgroundColor(svgContent);
        setDetectedBackgroundColor(bgColor);

        // Load color mappings for this design
        const mappings =
          (sideColorMappings[currentView] &&
            sideColorMappings[currentView][selectedDesignId]) ||
          {};
        setColorMappings(mappings);

        // Update transparent colors based on mappings
        const transparents = new Set();
        Object.entries(mappings).forEach(([color, mapped]) => {
          if (mapped === 'transparent') {
            transparents.add(color);
          }
        });

        if (transparents.size > 0) {
          setTransparentColors(transparents);
          // Check if the background is transparent
          if (bgColor && transparents.has(bgColor)) {
          }
        } else {
          // No transparent colors for this design
          setTransparentColors(new Set());
        }
      }
    }
  }, [currentView, selectedDesignId, sideSvgContent, sideColorMappings]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select first design when component mounts if there are designs
  useEffect(() => {
    if (!selectedDesignId) {
      // Find the first side that has designs
      const sides = ['front', 'back', 'side'];
      for (const side of sides) {
        if (sideDesigns[side] && sideDesigns[side].length > 0) {
          setCurrentView(side);
          setSelectedDesignId(sideDesigns[side][0].id);
          // Auto-selecting first design
          break;
        }
      }
    }
  }, []); // Only run on mount

  const fetchProductColors = useCallback(async () => {
    if (!formData.product) return;

    try {
      setIsLoadingColors(true);

      // The API is on a different domain, so we'll use a proxy endpoint
      const response = await fetch(`/products/${formData.product.id}/colors`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.colors) {
        // If we have a selected color, ensure it's in the list
        if (formData.productColor && formData.productColor.id !== 'default') {
          const selectedColorExists = data.colors.some(
            color => String(color.id) === String(formData.productColor.id)
          );

          if (!selectedColorExists) {
            // Add the current color to the list if it's not there
            const colorsWithSelected = [...data.colors, formData.productColor];
            setAvailableColors(colorsWithSelected);
            return;
          }
        }

        setAvailableColors(data.colors);
      }
    } catch (error) {
      // If fetch fails, at least keep the current color if we have one
      if (formData.productColor && formData.productColor.id !== 'default') {
        setAvailableColors([formData.productColor]);
      }
    } finally {
      setIsLoadingColors(false);
    }
  }, [formData.product, formData.productColor]);

  const fetchProductViews = useCallback(async () => {
    if (!formData.product || !formData.product.style) return;

    try {
      setIsLoadingViews(true);
      // Reset image loading states when fetching new views
      setProductImageError(false);
      // Combine brand name and style for S&S API (e.g., "Comfort Colors 1717")
      const styleWithBrand = formData.product.brandName
        ? `${formData.product.brandName} ${formData.product.style}`
        : formData.product.style;

      const response = await fetch(
        `/ss_products/product_images?stylename=${encodeURIComponent(
          styleWithBrand
        )}`
      );
      const data = await response.json();

      if (data.success) {
        setProductViews(data.images || {});
        if (data.message) {
        }

        // Log available views for debugging
        if (Object.keys(data.images || {}).length > 0) {
        }
      } else {
        // Don't show view buttons if S&S data isn't available
      }
    } catch (error) {
    } finally {
      setIsLoadingViews(false);
    }
  }, [formData.product]);

  const fetchAndParseSVG = useCallback(
    async (designToFetch = null, targetSide = 'front', forPool = false) => {
      const design = designToFetch || formData.design;

      if (!design || !design.id) {
        setIsLoading(false);
        return;
      }

      // Check if we've already fetched this design for this side
      const fetchKey = forPool
        ? `pool-${design.id}`
        : `${targetSide}-${design.id}`;
      if (fetchedDesignsRef.current.has(fetchKey)) {
        return;
      }

      fetchedDesignsRef.current.add(fetchKey); // Mark as fetched

      try {
        // Don't set loading state here - it's causing issues

        // Use the proxy endpoint to fetch SVG content
        const response = await fetch(`/designs/${design.id}/svg_content`);
        const data = await response.json();

        if (data.success) {
          // Clean the SVG content
          const svgText = data.svg_content
            .replace(/\[@tags=\[\]\]>/g, '')
            .replace(/\[>/g, '')
            .replace(/\]>/g, '')
            .replace(/<image[^>]*xlink:href="[^"]*"[^>]*>/g, '')
            .replace(/href="http[^"]*"/g, 'href="#"');

          if (forPool) {
            // Update design pool SVG content
            setDesignPoolSvgContent(prev => ({
              ...prev,
              [design.id]: svgText,
            }));
          } else {
            // Update sideSvgContent for the specific design
            setSideSvgContent(prev => ({
              ...prev,
              [targetSide]: {
                ...prev[targetSide],
                [design.id]: svgText,
              },
            }));

            // If this design is currently selected, update the main SVG content
            if (
              targetSide === currentView &&
              String(selectedDesignId) === String(design.id)
            ) {
              setSvgContent(svgText);
            }
          }

          // Extract unique colors from the SVG
          const colors = extractColorsFromSVG(svgText);

          // Detect background color
          const bgColor = detectBackgroundColor(svgText);

          // Only update colors if this is the currently selected design
          if (
            targetSide === currentView &&
            String(selectedDesignId) === String(design.id)
          ) {
            setDetectedColors(colors);
            setDetectedBackgroundColor(bgColor);
          }

          // Initialize color mappings for this design, preserving existing mappings
          const existingMappings =
            (sideColorMappings[targetSide] &&
              sideColorMappings[targetSide][design.id]) ||
            {};
          const initialMappings = {};
          colors.forEach(color => {
            // Preserve existing mapping if it exists, otherwise map to itself
            initialMappings[color] = existingMappings[color] || color;
          });

          // Only update if mappings don't already exist
          if (
            !sideColorMappings[targetSide] ||
            !sideColorMappings[targetSide][design.id]
          ) {
            setSideColorMappings(prev => ({
              ...prev,
              [targetSide]: {
                ...prev[targetSide],
                [design.id]: initialMappings,
              },
            }));
          }

          // If this design is currently selected, update the main color mappings
          if (
            targetSide === currentView &&
            String(selectedDesignId) === String(design.id)
          ) {
            // Use existing mappings if available
            const currentMappings =
              existingMappings && Object.keys(existingMappings).length > 0
                ? existingMappings
                : initialMappings;
            setColorMappings(currentMappings);

            // Also restore transparent colors if they exist
            const transparents = new Set();
            Object.entries(currentMappings).forEach(([color, mapped]) => {
              if (mapped === 'transparent') {
                transparents.add(color);
              }
            });
            if (transparents.size > 0) {
              setTransparentColors(transparents);
            }
          }
        } else {
          // Check if the error is because it's not an SVG
          if (data.error && data.error.includes('not in SVG format')) {
            // Mark this design as PNG in our state
            setPngDesigns(prev => new Set([...prev, String(design.id)]));

            // Don't show this as an error - the design can still be placed
            // but we'll show a notice about ink color customization
          } else {
            setError('Failed to load design');
          }
        }
      } catch (err) {
        setError('Error loading design. Please try again.');
      } finally {
        // setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      formData.design,
      currentView,
      selectedDesignId,
      sideColorMappings,
      // Note: extractColorsFromSVG and detectBackgroundColor are excluded because:
      // 1. They are wrapped in useCallback with stable references
      // 2. They are defined later in the file (would cause "used before defined" errors)
      setIsLoading,
      setDesignPoolSvgContent,
      setSideSvgContent,
      setSvgContent,
      setDetectedColors,
      setDetectedBackgroundColor,
      setSideColorMappings,
      setColorMappings,
      setTransparentColors,
      setPngDesigns,
      setError,
      fetchedDesignsRef,
    ]
  );

  // Check if SVG content contains rasterized components
  const hasRasterizedContent = svgContent => {
    if (!svgContent) return false;

    return (
      // Check for <image> tags
      /<image[^>]*>/i.test(svgContent) ||
      // Check for embedded base64 images
      /data:image\/(png|jpeg|jpg|gif|webp)/i.test(svgContent) ||
      // Check for external image references
      /xlink:href=["'][^"']*\.(png|jpg|jpeg|gif|webp)["']/i.test(svgContent) ||
      // Check for href attribute with image extensions (SVG 2.0 syntax)
      /href=["'][^"']*\.(png|jpg|jpeg|gif|webp)["']/i.test(svgContent)
    );
  };

  // Calculate aspect ratio from SVG content
  const getAspectRatioFromSVG = svgText => {
    try {
      // Create a temporary div to parse the SVG
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = svgText;
      const svgElement = tempDiv.querySelector('svg');

      if (svgElement) {
        // Try to get viewBox dimensions
        const viewBox = svgElement.getAttribute('viewBox');
        if (viewBox) {
          const [x, y, width, height] = viewBox.split(/\s+/).map(Number);
          if (width && height) {
            // Check if there are any visible elements in the SVG
            const visibleElements = svgElement.querySelectorAll(
              'rect:not([fill="none"]):not([fill="transparent"]), circle, ellipse, path:not([fill="none"]):not([fill="transparent"]), polygon, polyline, text, image'
            );

            if (visibleElements.length > 0) {
              // Calculate the actual bounding box of visible content
              let minX = Infinity,
                minY = Infinity,
                maxX = -Infinity,
                maxY = -Infinity;
              let hasValidBounds = false;

              // Simple parsing of common shape coordinates
              visibleElements.forEach(el => {
                const tagName = el.tagName.toLowerCase();

                if (tagName === 'rect') {
                  const x = parseFloat(el.getAttribute('x') || 0);
                  const y = parseFloat(el.getAttribute('y') || 0);
                  const w = parseFloat(el.getAttribute('width') || 0);
                  const h = parseFloat(el.getAttribute('height') || 0);
                  if (w > 0 && h > 0) {
                    minX = Math.min(minX, x);
                    minY = Math.min(minY, y);
                    maxX = Math.max(maxX, x + w);
                    maxY = Math.max(maxY, y + h);
                    hasValidBounds = true;
                  }
                } else if (tagName === 'circle') {
                  const cx = parseFloat(el.getAttribute('cx') || 0);
                  const cy = parseFloat(el.getAttribute('cy') || 0);
                  const r = parseFloat(el.getAttribute('r') || 0);
                  if (r > 0) {
                    minX = Math.min(minX, cx - r);
                    minY = Math.min(minY, cy - r);
                    maxX = Math.max(maxX, cx + r);
                    maxY = Math.max(maxY, cy + r);
                    hasValidBounds = true;
                  }
                }
                // For complex paths, we'll use the viewBox as approximation
              });

              if (hasValidBounds && isFinite(minX) && isFinite(maxX)) {
                const actualWidth = maxX - minX;
                const actualHeight = maxY - minY;
                if (actualHeight > 0) {
                  return actualWidth / actualHeight;
                }
              }
            }

            // Fallback to viewBox aspect ratio
            return width / height;
          }
        }

        // Try width/height attributes
        const width = parseFloat(svgElement.getAttribute('width') || 0);
        const height = parseFloat(svgElement.getAttribute('height') || 0);
        if (width && height) {
          return width / height;
        }
      }
    } catch (e) {}
    return 1; // Default to square
  };

  const normalizeColor = useCallback(color => {
    if (!color || color === 'none' || color === 'transparent') return '#ffffff';

    // Handle hex colors
    if (color.match(/^#[0-9A-Fa-f]{6}$/i)) {
      return color.toUpperCase();
    } else if (color.match(/^#[0-9A-Fa-f]{3}$/i)) {
      // Convert 3-digit hex to 6-digit
      return `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${
        color[3]
      }`.toUpperCase();
    } else if (color.match(/^[0-9A-Fa-f]{6}$/i)) {
      return `#${color}`.toUpperCase();
    } else {
      // For named colors or other formats, default to white
      return '#ffffff';
    }
  }, []);

  const detectBackgroundColor = useCallback(
    svgText => {
      try {
        // 1. Background rectangle with fill (100% width/height)
        let match = svgText.match(
          /<rect[^>]*width=["']100%["'][^>]*height=["']100%["'][^>]*fill=["']([^"']+)["']/
        );
        if (match) {
          const color = normalizeColor(match[1]);
          return color; // Return even if white - user can decide to remove it
        }

        // 2. First rectangle in the SVG (often the background)
        match = svgText.match(/<rect[^>]*fill=["']([^"']+)["']/);
        if (match) {
          const color = normalizeColor(match[1]);
          // Check if this rect is large by parsing attributes
          const rectMatch = svgText.match(/<rect[^>]*>/);
          if (rectMatch) {
            const rectTag = rectMatch[0];
            const widthMatch = rectTag.match(/width=["']([^"']+)["']/);
            const heightMatch = rectTag.match(/height=["']([^"']+)["']/);

            if (widthMatch && heightMatch) {
              // Parse viewBox to get SVG dimensions
              const viewBoxMatch = svgText.match(/viewBox=["']([^"']+)["']/);
              if (viewBoxMatch) {
                const [, , svgWidth, svgHeight] = viewBoxMatch[1]
                  .split(/\s+/)
                  .map(Number);
                const rectWidth = parseFloat(widthMatch[1]);
                const rectHeight = parseFloat(heightMatch[1]);

                // If rect covers most of the viewBox
                if (
                  rectWidth >= svgWidth * 0.8 &&
                  rectHeight >= svgHeight * 0.8
                ) {
                  return color;
                }
              }
            }
          }
        }

        // 3. Parse SVG and look for the largest rectangle
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
        const svg = svgDoc.querySelector('svg');

        if (svg) {
          const viewBox = svg.getAttribute('viewBox');
          let svgWidth, svgHeight;

          if (viewBox) {
            const [, , width, height] = viewBox.split(' ').map(Number);
            svgWidth = width;
            svgHeight = height;
          } else {
            svgWidth = parseFloat(svg.getAttribute('width') || 100);
            svgHeight = parseFloat(svg.getAttribute('height') || 100);
          }

          // Find all rectangles and sort by area
          const rects = Array.from(svg.querySelectorAll('rect'));
          let largestRect = null;
          let largestArea = 0;

          rects.forEach(rect => {
            const width = parseFloat(rect.getAttribute('width') || 0);
            const height = parseFloat(rect.getAttribute('height') || 0);
            const area = width * height;

            if (area > largestArea) {
              largestArea = area;
              largestRect = rect;
            }
          });

          // If the largest rectangle covers significant area
          if (largestRect && largestArea >= svgWidth * svgHeight * 0.5) {
            const fill = largestRect.getAttribute('fill');
            if (fill) {
              const color = normalizeColor(fill);
              return color;
            }
          }

          // 4. Check first element if it's a path that might be background
          const firstElement = svg.firstElementChild;
          if (firstElement && firstElement.tagName === 'path') {
            const fill = firstElement.getAttribute('fill');
            if (fill) {
              const color = normalizeColor(fill);
              return color;
            }
          }
        }
      } catch (err) {}

      return null;
    },
    [normalizeColor]
  );

  const extractColorsFromSVG = useCallback(
    svgText => {
      const colors = new Set();

      // Helper function to normalize colors
      const addColor = (color, source) => {
        if (!color) return;

        // Skip 'none' and 'transparent'
        if (color === 'none' || color === 'transparent') return;

        // Handle hex colors
        if (color.startsWith('#')) {
          const normalized = normalizeColor(color);
          colors.add(normalized);
        }
        // Handle named colors
        else if (
          color === 'black' ||
          color === 'rgb(0,0,0)' ||
          color === 'rgb(0, 0, 0)'
        ) {
          colors.add('#000000');
        } else if (
          color === 'white' ||
          color === 'rgb(255,255,255)' ||
          color === 'rgb(255, 255, 255)'
        ) {
          colors.add('#FFFFFF');
        }
        // Handle other common named colors
        else if (color.match(/^[a-zA-Z]+$/)) {
          const namedColors = {
            black: '#000000',
            white: '#FFFFFF',
            red: '#FF0000',
            green: '#008000',
            blue: '#0000FF',
            yellow: '#FFFF00',
            cyan: '#00FFFF',
            magenta: '#FF00FF',
            gray: '#808080',
            grey: '#808080',
          };
          if (namedColors[color.toLowerCase()]) {
            colors.add(namedColors[color.toLowerCase()]);
          }
        } else {
        }
      };

      // Extract colors from fill attributes
      const fillMatches = svgText.match(/fill="([^"]+)"/g) || [];
      fillMatches.forEach(match => {
        const color = match.match(/fill="([^"]+)"/)[1];
        addColor(color, 'fill attribute');
      });

      // Extract colors from stroke attributes
      const strokeMatches = svgText.match(/stroke="([^"]+)"/g) || [];
      strokeMatches.forEach(match => {
        const color = match.match(/stroke="([^"]+)"/)[1];
        addColor(color, 'stroke attribute');
      });

      // Extract colors from style attributes
      const styleMatches =
        svgText.match(/style="[^"]*(?:fill|stroke):\s*([^;}"]+)[^"]*"/g) || [];
      styleMatches.forEach(match => {
        const colorMatch = match.match(/(?:fill|stroke):\s*([^;}"]+)/);
        if (colorMatch) {
          addColor(colorMatch[1].trim(), 'style attribute');
        }
      });

      // Also check for fill/stroke without quotes (common in some SVGs)
      const fillNoQuotes = svgText.match(/fill=([#\w]+)(?:\s|>)/g) || [];
      fillNoQuotes.forEach(match => {
        const color = match.match(/fill=([#\w]+)/)[1];
        addColor(color, 'fill no quotes');
      });

      const strokeNoQuotes = svgText.match(/stroke=([#\w]+)(?:\s|>)/g) || [];
      strokeNoQuotes.forEach(match => {
        const color = match.match(/stroke=([#\w]+)/)[1];
        addColor(color, 'stroke no quotes');
      });

      return Array.from(colors);
    },
    [normalizeColor]
  );

  const updateColorMapping = (originalColor, newColor) => {
    if (!selectedDesignId || !currentView) return;

    // Update the global color mappings (for backward compatibility)
    const newMappings = {
      ...colorMappings,
      [originalColor]: newColor,
    };
    setColorMappings(newMappings);

    // Update the side-specific color mappings for the selected design
    const newSideColorMappings = {
      ...sideColorMappings,
      [currentView]: {
        ...(sideColorMappings[currentView] || {}),
        [selectedDesignId]: {
          ...((sideColorMappings[currentView] &&
            sideColorMappings[currentView][selectedDesignId]) ||
            {}),
          [originalColor]: newColor,
        },
      },
    };
    setSideColorMappings(newSideColorMappings);

    // Auto-save to formData with the new mappings
    saveCustomizationState(
      newMappings,
      transparentColors,
      designPositions,
      designSizes,
      designPool,
      newSideColorMappings
    );
  };

  // Auto-save customization state to localStorage
  const saveCustomizationState = (
    newColorMappings = null,
    newTransparentColors = null,
    newPositions = null,
    newSizes = null,
    newDesignPool = null,
    newSideColorMappings = null
  ) => {
    const mappings = newColorMappings || colorMappings;
    const transparent = newTransparentColors || transparentColors;
    const positions = newPositions || designPositions;
    const sizes = newSizes || designSizes;
    const pool = newDesignPool || designPool;
    const sideMappings = newSideColorMappings || sideColorMappings;

    console.log(
      '[DesignCustomizer] saveCustomizationState called with pool:',
      pool
    );

    const cleanSideDesigns = createCleanDesignReferences(sideDesigns);

    const customizationData = {
      colorMappings: mappings,
      transparentColors: Array.from(transparent),
      positions: positions,
      sizes: sizes,
      // Keep backward compatibility
      position: positions.front,
      size: sizes.front,
      currentView: currentView,
      // Store clean design references without SVG content
      sideDesigns: cleanSideDesigns,
      sideColorMappings: sideMappings,
      // Store design pool
      designPool: pool.map(design => ({
        id: design.id,
        artCode: design.artCode,
        url: design.url || design.thumbnailUrl,
        thumbnailUrl: design.thumbnailUrl || design.url,
        svgUrl: design.svgUrl || `/designs/${design.id}/svg_content`,
      })),
      // Store product views for preview rendering
      productViews: productViews,
      // Store SVG content for preview rendering
      sideSvgContent: sideSvgContent,
      designPoolSvgContent: designPoolSvgContent,
      // Store detected colors
      detectedColors: detectedColors,
      // Don't store customizedSvg to avoid quota issues
    };
    // Saving customization
    handleChange('designCustomization', customizationData);
  };

  const toggleTransparent = color => {
    if (!selectedDesignId || !currentView) return;

    const newSet = new Set(transparentColors);
    if (newSet.has(color)) {
      newSet.delete(color);
    } else {
      newSet.add(color);
    }
    setTransparentColors(newSet);

    // Update color mappings for the selected design
    const newColorMappings = { ...colorMappings };
    newColorMappings[color] = newSet.has(color) ? 'transparent' : color;
    setColorMappings(newColorMappings);

    // Update side color mappings - fix: only update the specific color, not all mappings
    const newSideColorMappings = {
      ...sideColorMappings,
      [currentView]: {
        ...(sideColorMappings[currentView] || {}),
        [selectedDesignId]: {
          ...((sideColorMappings[currentView] &&
            sideColorMappings[currentView][selectedDesignId]) ||
            {}),
          [color]: newSet.has(color) ? 'transparent' : color,
        },
      },
    };
    setSideColorMappings(newSideColorMappings);

    // Auto-save to formData
    saveCustomizationState(
      newColorMappings,
      newSet,
      designPositions,
      designSizes,
      null,
      newSideColorMappings
    );
  };

  const resetDesignColors = () => {
    if (!selectedDesignId) return;

    // Reset all color mappings to original colors
    const resetMappings = {};
    detectedColors.forEach(color => {
      resetMappings[color] = color;
    });

    // Clear all modifications
    setColorMappings(resetMappings);
    setTransparentColors(new Set());
    setSelectedColor(null);

    // Reset side-specific color mappings for selected design
    let newSideColorMappings = sideColorMappings;
    if (currentView && selectedDesignId) {
      newSideColorMappings = {
        ...sideColorMappings,
        [currentView]: {
          ...(sideColorMappings[currentView] || {}),
          [selectedDesignId]: resetMappings,
        },
      };
      setSideColorMappings(newSideColorMappings);
    }

    // Save the reset state
    saveCustomizationState(
      resetMappings,
      new Set(),
      designPositions,
      designSizes,
      designPool,
      newSideColorMappings
    );
  };

  const getColorVariants = useCallback(hexColor => {
    const variants = [hexColor];
    if (hexColor === '#000000') {
      variants.push('black', 'rgb(0,0,0)', 'rgb(0, 0, 0)');
    } else if (hexColor === '#FFFFFF') {
      variants.push('white', 'rgb(255,255,255)', 'rgb(255, 255, 255)');
    }
    return variants;
  }, []);

  const getPreviewSVGForDesign = useCallback(
    (side, designId) => {
      const designSvgs = sideSvgContent[side] || {};
      const designMappings = sideColorMappings[side] || {};

      if (!designId || !designSvgs[designId]) return '';

      let modifiedSvg = designSvgs[designId];

      // Ensure the SVG fills its container while maintaining aspect ratio
      modifiedSvg = modifiedSvg.replace(/<svg([^>]*)>/, (match, attrs) => {
        // Remove any width/height attributes and add preserveAspectRatio
        const cleanedAttrs = attrs
          .replace(/width=["'][^"']*["']/gi, '')
          .replace(/height=["'][^"']*["']/gi, '');
        return `<svg${cleanedAttrs} width="100%" height="100%" preserveAspectRatio="xMidYMid meet">`;
      });

      // ALWAYS remove background - detect and remove it first
      const bgColor = detectBackgroundColor(modifiedSvg);
      if (bgColor) {
        // Apply background removal patterns
        const variants = getColorVariants(bgColor);
        variants.forEach(variant => {
          modifiedSvg = modifiedSvg.replace(
            new RegExp(`fill="${variant}"`, 'gi'),
            'fill="transparent"'
          );
          modifiedSvg = modifiedSvg.replace(
            new RegExp(`stroke="${variant}"`, 'gi'),
            'stroke="transparent"'
          );
          modifiedSvg = modifiedSvg.replace(
            new RegExp(`(fill|stroke):\\s*${variant}`, 'gi'),
            '$1: transparent'
          );
        });
      }

      // Also apply pattern-based background removal
      // Method 1: Replace fill in Apparel_Color rect elements
      modifiedSvg = modifiedSvg.replace(
        /(<g\s+id="Apparel_Color"[^>]*>[\s\S]*?<rect[^>]+)fill="[^"]*"/gi,
        '$1fill="transparent"'
      );

      // Method 2: First rect with specific dimensions
      modifiedSvg = modifiedSvg.replace(
        /(<rect[^>]+width="972"[^>]+height="1152"[^>]+)fill="[^"]*"/gi,
        '$1fill="transparent"'
      );

      // Method 3: Background rect pattern
      modifiedSvg = modifiedSvg.replace(
        /<rect\s+x="-[\d.]+"[^>]+width="972"[^>]+fill="#[^"]+"/gi,
        match => match.replace(/fill="#[^"]+"/, 'fill="transparent"')
      );

      // Method 4: First large rect that covers most of the viewBox
      modifiedSvg = modifiedSvg.replace(
        /<rect\s+(?:x="0"\s+)?(?:y="0"\s+)?width="100%"\s+height="100%"[^>]*fill="[^"]+"/gi,
        match => match.replace(/fill="[^"]+"/, 'fill="transparent"')
      );

      const mappings = designMappings[designId] || {};

      // Debug log
      const transparentColors = Object.entries(mappings).filter(
        ([_, v]) => v === 'transparent'
      );
      if (transparentColors.length > 0) {
      }

      // Apply color mappings for this design
      Object.entries(mappings).forEach(([original, replacement]) => {
        if (original !== replacement) {
          const variants = getColorVariants(original);
          variants.forEach(variant => {
            // Handle transparent color specially
            const replaceValue =
              replacement === 'transparent' ? 'transparent' : replacement;

            modifiedSvg = modifiedSvg.replace(
              new RegExp(`fill="${variant}"`, 'gi'),
              `fill="${replaceValue}"`
            );
            modifiedSvg = modifiedSvg.replace(
              new RegExp(`stroke="${variant}"`, 'gi'),
              `stroke="${replaceValue}"`
            );
            modifiedSvg = modifiedSvg.replace(
              new RegExp(`(fill|stroke):\\s*${variant}`, 'gi'),
              `$1: ${replaceValue}`
            );
          });
        }
      });

      return modifiedSvg;
    },
    [sideSvgContent, sideColorMappings, detectBackgroundColor, getColorVariants]
  );

  // Removed getPreviewSVG - we use getPreviewSVGForSide instead

  // Removed captureDesignPreview to avoid storing SVG content

  const handleColorSwitch = color => {
    // Update the product color in formData
    const newProductColor = {
      id: color.id,
      name: color.name,
      hexCode: color.hexCode ? `#${color.hexCode}` : color.hexCode,
      marketingImageUrl: color.marketingImageUrl,
      marketing_image_url: color.marketingImageUrl, // Add snake_case version too
      hex_code: color.hexCode ? `#${color.hexCode}` : color.hexCode, // Add snake_case version
    };

    handleChange('productColor', newProductColor);

    try {
      const currentFormData = JSON.parse(
        localStorage.getItem('formData') || '{}'
      );
      currentFormData.productColor = newProductColor;
      localStorage.setItem('formData', JSON.stringify(currentFormData));
    } catch (e) {}

    // Force update the available colors to include this selection
    setAvailableColors(prevColors => {
      const colorExists = prevColors.some(
        c => String(c.id) === String(color.id)
      );
      if (!colorExists) {
        return [...prevColors, color];
      }
      return prevColors;
    });

    // Stay on current view when switching colors
  };

  const handleMoveDesign = (designId, targetSide) => {
    // Find which side currently has this design
    let sourceSide = null;
    let sourceDesign = null;

    for (const [side, designs] of Object.entries(sideDesigns)) {
      if (Array.isArray(designs)) {
        const design = designs.find(d => String(d.id) === String(designId));
        if (design) {
          sourceSide = side;
          sourceDesign = design;
          break;
        }
      }
    }

    if (!sourceSide || !sourceDesign) return;

    // Remove from source side
    const newSourceDesigns = sideDesigns[sourceSide].filter(
      d => String(d.id) !== String(designId)
    );

    // Add to target side (ensure it's an array)
    const targetDesigns = Array.isArray(sideDesigns[targetSide])
      ? sideDesigns[targetSide]
      : [];
    const newTargetDesigns = [...targetDesigns, sourceDesign];

    const newSideDesigns = {
      ...sideDesigns,
      [sourceSide]: newSourceDesigns,
      [targetSide]: newTargetDesigns,
    };

    // Move SVG content
    const sourceSvg =
      sideSvgContent[sourceSide] && sideSvgContent[sourceSide][designId];
    const newSideSvgContent = { ...sideSvgContent };

    // Remove from source
    if (newSideSvgContent[sourceSide]) {
      delete newSideSvgContent[sourceSide][designId];
    }

    // Add to target
    if (!newSideSvgContent[targetSide]) {
      newSideSvgContent[targetSide] = {};
    }
    if (sourceSvg) {
      newSideSvgContent[targetSide][designId] = sourceSvg;
    }

    // Move color mappings
    const sourceColorMappings =
      sideColorMappings[sourceSide] && sideColorMappings[sourceSide][designId];
    const newSideColorMappings = { ...sideColorMappings };

    // Remove from source
    if (newSideColorMappings[sourceSide]) {
      delete newSideColorMappings[sourceSide][designId];
    }

    // Add to target
    if (!newSideColorMappings[targetSide]) {
      newSideColorMappings[targetSide] = {};
    }
    if (sourceColorMappings) {
      newSideColorMappings[targetSide][designId] = sourceColorMappings;
    }

    // Move position/size data
    const sourceKey = `${sourceSide}-${designId}`;
    const targetKey = `${targetSide}-${designId}`;

    const newPositions = { ...designPositions };
    const newSizes = { ...designSizes };

    // Copy position/size to new key
    if (designPositions[sourceKey]) {
      newPositions[targetKey] = designPositions[sourceKey];
      delete newPositions[sourceKey];
    } else {
      // Default position if not set
      newPositions[targetKey] = { x: 50, y: 50 };
    }

    if (designSizes[sourceKey]) {
      newSizes[targetKey] = designSizes[sourceKey];
      delete newSizes[sourceKey];
    } else {
      // Default size if not set
      newSizes[targetKey] = { width: 40, height: 40 };
    }

    // Update all states
    setSideDesigns(newSideDesigns);
    setSideSvgContent(newSideSvgContent);
    setSideColorMappings(newSideColorMappings);
    setDesignPositions(newPositions);
    setDesignSizes(newSizes);

    // Update localStorage
    updateLocalStorageDesigns(newSideDesigns);

    // Update form data
    handleChange('designCustomization', {
      sideDesigns: createCleanDesignReferences(newSideDesigns),
      sideColorMappings: newSideColorMappings,
      positions: newPositions,
      sizes: newSizes,
      currentView: currentView,

      // Design pool
      designPool: designPool.map(design => ({
        id: design.id,
        artCode: design.artCode,
        url: design.url || design.thumbnailUrl,
        thumbnailUrl: design.thumbnailUrl || design.url,
        svgUrl: design.svgUrl || `/designs/${design.id}/svg_content`,
      })),

      // Keep backward compatibility for single design
      colorMappings: colorMappings,
      transparentColors: Array.from(transparentColors),
      position: designPositions.front || { x: 50, y: 50 },
      size: designSizes.front || { width: 40, height: 40 },
      // Don't store SVG content to avoid quota issues
    });

    // If we moved to the current view, select this design
    if (targetSide === currentView) {
      setSelectedDesignId(designId);
    }
  };

  const handleRemoveFromPool = designId => {
    // Remove design from pool
    const newDesignPool = designPool.filter(
      d => String(d.id) !== String(designId)
    );
    setDesignPool(newDesignPool);

    // Remove SVG content for this design
    const newDesignPoolSvgContent = { ...designPoolSvgContent };
    delete newDesignPoolSvgContent[designId];
    setDesignPoolSvgContent(newDesignPoolSvgContent);

    // Check if this is the main design from formData.design
    const isMainDesign =
      formData.design && String(formData.design.id) === String(designId);

    // Add this design to removedDesigns set to prevent it from being restored
    setRemovedDesigns(prev => new Set([...prev, String(designId)]));

    // Remove design from all sides
    const newSideDesigns = { ...sideDesigns };
    const newSideSvgContent = { ...sideSvgContent };
    const newSideColorMappings = { ...sideColorMappings };
    const newPositions = { ...designPositions };
    const newSizes = { ...designSizes };

    ['front', 'side', 'back'].forEach(side => {
      if (newSideDesigns[side]) {
        // Remove design from this side
        newSideDesigns[side] = newSideDesigns[side].filter(
          d => String(d.id) !== String(designId)
        );

        // Remove SVG content for this design on this side
        if (newSideSvgContent[side] && newSideSvgContent[side][designId]) {
          delete newSideSvgContent[side][designId];
        }

        // Remove color mappings for this design on this side
        if (
          newSideColorMappings[side] &&
          newSideColorMappings[side][designId]
        ) {
          delete newSideColorMappings[side][designId];
        }

        // Remove position/size data for this design on this side
        const designKey = `${side}-${designId}`;
        delete newPositions[designKey];
        delete newSizes[designKey];
      }
    });

    // Update all states
    setSideDesigns(newSideDesigns);
    setSideSvgContent(newSideSvgContent);
    setSideColorMappings(newSideColorMappings);
    setDesignPositions(newPositions);
    setDesignSizes(newSizes);

    // If the removed design was selected, clear selection
    if (String(selectedDesignId) === String(designId)) {
      setSelectedDesignId(null);
    }

    // Immediately update the form data
    const updatedCustomization = {
      ...formData.designCustomization,
      designPool: newDesignPool.map(design => ({
        id: design.id,
        artCode: design.artCode,
        url: design.url || design.thumbnailUrl,
        thumbnailUrl: design.thumbnailUrl || design.url,
        svgUrl: design.svgUrl || `/designs/${design.id}/svg_content`,
      })),
      designPoolSvgContent: newDesignPoolSvgContent,
      // Include other current state
      sideDesigns: createCleanDesignReferences(sideDesigns),
      sideColorMappings: sideColorMappings,
      positions: designPositions,
      sizes: designSizes,
      currentView: currentView,
      colorMappings: colorMappings,
      transparentColors: Array.from(transparentColors),
      position: designPositions.front || { x: 50, y: 50 },
      size: designSizes.front || { width: 40, height: 40 },
    };

    // Update through handleChange to ensure parent component gets the update
    handleChange('designCustomization', updatedCustomization);

    // If this was the main design, clear it from formData.design
    if (isMainDesign) {
      handleChange('design', null);
      // Mark this design as processed so it won't be re-added
      processedDesignsRef.current.add(String(designId));
    }

    // Also update localStorage immediately
    const currentFormData = JSON.parse(
      localStorage.getItem('formData') || '{}'
    );
    currentFormData.designCustomization = updatedCustomization;
    if (isMainDesign) {
      currentFormData.design = null;
    }
    localStorage.setItem('formData', JSON.stringify(currentFormData));

    // Clear any selectedDesigns in storage that might cause re-adding
    try {
      const selectedDesigns = JSON.parse(
        localStorage.getItem('selectedDesigns') || '[]'
      );
      const filteredDesigns = selectedDesigns.filter(
        d => String(d.id) !== String(designId)
      );
      if (filteredDesigns.length > 0) {
        localStorage.setItem(
          'selectedDesigns',
          JSON.stringify(filteredDesigns)
        );
      } else {
        localStorage.removeItem('selectedDesigns');
      }

      // Also check sessionStorage
      const sessionDesigns = JSON.parse(
        sessionStorage.getItem('selectedDesigns') || '[]'
      );
      const filteredSessionDesigns = sessionDesigns.filter(
        d => String(d.id) !== String(designId)
      );
      if (filteredSessionDesigns.length > 0) {
        sessionStorage.setItem(
          'selectedDesigns',
          JSON.stringify(filteredSessionDesigns)
        );
      } else {
        sessionStorage.removeItem('selectedDesigns');
      }
    } catch (e) {
      // Ignore parsing errors
    }
  };

  const handleAddDesignFromPoolToSide = useCallback(
    (designId, side) => {
      // Find the design in the pool
      const design = designPool.find(d => String(d.id) === String(designId));
      if (!design) {
        return;
      }

      // Add design to the specified side
      const newSideDesigns = {
        ...sideDesigns,
        [side]: [
          ...(Array.isArray(sideDesigns[side]) ? sideDesigns[side] : []),
          design,
        ],
      };

      setSideDesigns(newSideDesigns);

      // Copy SVG content if available
      if (designPoolSvgContent[design.id]) {
        setSideSvgContent(prev => ({
          ...prev,
          [side]: {
            ...prev[side],
            [design.id]: designPoolSvgContent[design.id],
          },
        }));
      }

      // Initialize position and size for this design on this side
      const designKey = `${side}-${design.id}`;
      const newPositions = {
        ...designPositions,
        [designKey]: { x: 50, y: 50 },
      };
      const newSizes = {
        ...designSizes,
        [designKey]: { width: 40, height: 40 },
      };

      setDesignPositions(newPositions);
      setDesignSizes(newSizes);

      // The auto-save effect will handle persisting these changes
      // This ensures all state updates are complete before saving

      // If this is the current view, select the design
      if (side === currentView) {
        setSelectedDesignId(design.id);
      }
    },
    [
      designPool,
      sideDesigns,
      designPoolSvgContent,
      designPositions,
      designSizes,
      currentView,
    ]
  );

  const handleRemoveDesignFromSide = (side, designId) => {
    // If no designId provided, it's the old behavior (remove all from side)
    if (!designId) {
      const newSideDesigns = {
        ...sideDesigns,
        [side]: [],
      };
      setSideDesigns(newSideDesigns);
      setSideSvgContent(prev => ({
        ...prev,
        [side]: {},
      }));
      setSideColorMappings(prev => ({
        ...prev,
        [side]: {},
      }));

      // Update localStorage
      updateLocalStorageDesigns(newSideDesigns);

      // Update form data to persist changes
      handleChange('designCustomization', {
        sideDesigns: createCleanDesignReferences(newSideDesigns),
        sideColorMappings: { ...sideColorMappings, [side]: {} },
        positions: designPositions,
        sizes: designSizes,
        currentView: currentView,

        // Design pool
        designPool: designPool.map(design => ({
          id: design.id,
          artCode: design.artCode,
          url: design.url || design.thumbnailUrl,
          thumbnailUrl: design.thumbnailUrl || design.url,
          svgUrl: design.svgUrl || `/designs/${design.id}/svg_content`,
        })),

        // Keep backward compatibility for single design
        colorMappings: colorMappings,
        transparentColors: Array.from(transparentColors),
        position: designPositions.front || { x: 50, y: 50 },
        size: designSizes.front || { width: 40, height: 40 },
        // Don't store SVG content to avoid quota issues
      });
      return;
    }

    // Remove specific design from side
    const currentDesigns = sideDesigns[side] || [];
    const removedDesign = currentDesigns.find(
      d => String(d.id) === String(designId)
    );
    const newDesigns = currentDesigns.filter(
      d => String(d.id) !== String(designId)
    );

    const newSideDesigns = {
      ...sideDesigns,
      [side]: newDesigns,
    };

    // If the design is not on any other side, add it back to the design pool
    const isOnOtherSides = Object.entries(sideDesigns).some(
      ([s, designs]) =>
        s !== side && designs.some(d => String(d.id) === String(designId))
    );

    if (
      !isOnOtherSides &&
      removedDesign &&
      !designPool.some(d => String(d.id) === String(designId))
    ) {
      // Add back to design pool
      setDesignPool(prev => [...prev, removedDesign]);

      // Remove from removedDesigns set since it's being re-added
      setRemovedDesigns(prev => {
        const newSet = new Set(prev);
        newSet.delete(String(designId));
        return newSet;
      });

      // Copy SVG content to pool if available
      if (sideSvgContent[side] && sideSvgContent[side][designId]) {
        setDesignPoolSvgContent(prev => ({
          ...prev,
          [designId]: sideSvgContent[side][designId],
        }));
      }
    }

    // Remove SVG content for this design
    const newSvgContent = { ...(sideSvgContent[side] || {}) };
    delete newSvgContent[designId];

    const newSideSvgContent = {
      ...sideSvgContent,
      [side]: newSvgContent,
    };

    // Remove color mappings for this design
    const newColorMappings = { ...(sideColorMappings[side] || {}) };
    delete newColorMappings[designId];

    const newSideColorMappings = {
      ...sideColorMappings,
      [side]: newColorMappings,
    };

    // Remove position/size data for this design
    const designKey = `${side}-${designId}`;
    const newPositions = { ...designPositions };
    delete newPositions[designKey];

    const newSizes = { ...designSizes };
    delete newSizes[designKey];

    // Update all states
    setSideDesigns(newSideDesigns);
    setSideSvgContent(newSideSvgContent);
    setSideColorMappings(newSideColorMappings);
    setDesignPositions(newPositions);
    setDesignSizes(newSizes);

    // Update localStorage
    updateLocalStorageDesigns(newSideDesigns);

    // Update form data to persist changes
    handleChange('designCustomization', {
      sideDesigns: createCleanDesignReferences(newSideDesigns),
      sideColorMappings: newSideColorMappings,
      positions: newPositions,
      sizes: newSizes,
      currentView: currentView,

      // Design pool
      designPool: designPool.map(design => ({
        id: design.id,
        artCode: design.artCode,
        url: design.url || design.thumbnailUrl,
        thumbnailUrl: design.thumbnailUrl || design.url,
        svgUrl: design.svgUrl || `/designs/${design.id}/svg_content`,
      })),

      // Keep backward compatibility for single design
      colorMappings: colorMappings,
      transparentColors: Array.from(transparentColors),
      position: designPositions.front || { x: 50, y: 50 },
      size: designSizes.front || { width: 40, height: 40 },
      // Don't store SVG content to avoid quota issues
    });

    // If we removed the selected design, select another one or null
    if (String(selectedDesignId) === String(designId)) {
      setSelectedDesignId(newDesigns.length > 0 ? newDesigns[0].id : null);
    }
  };

  const handleNext = async () => {
    // Save the customized design data with multi-side support
    handleChange('designCustomization', {
      // Multi-side design data
      sideDesigns: createCleanDesignReferences(sideDesigns),
      sideColorMappings: sideColorMappings,
      positions: designPositions,
      sizes: designSizes,
      currentView: currentView,

      // Design pool
      designPool: designPool.map(design => ({
        id: design.id,
        artCode: design.artCode,
        url: design.url || design.thumbnailUrl,
        thumbnailUrl: design.thumbnailUrl || design.url,
        svgUrl: design.svgUrl || `/designs/${design.id}/svg_content`,
      })),

      // Keep backward compatibility for single design
      colorMappings: colorMappings,
      transparentColors: Array.from(transparentColors),
      position: designPositions.front,
      size: designSizes.front,

      // Include data needed for drawer preview
      productViews: productViews,
      sideSvgContent: sideSvgContent,
      pngDesigns: Array.from(pngDesigns),
    });
    setModalStepNumber(modalStepNumber + 1);
  };

  const handleSkip = () => {
    // Clear any customization and proceed
    handleChange('designCustomization', null);
    setModalStepNumber(modalStepNumber + 1);
  };

  // Get colors used on other sides - computed after all helper functions
  const otherSideColors = useMemo(() => {
    const colors = {};
    const sides = ['front', 'back', 'side'];

    sides.forEach(side => {
      if (
        side !== currentView &&
        sideDesigns[side] &&
        sideDesigns[side].length > 0
      ) {
        const usedColors = new Set();

        // Get colors from all designs on this side
        sideDesigns[side].forEach(design => {
          if (!design || !design.id) return;

          // Get the preview SVG with color mappings applied
          const previewSvg = getPreviewSVGForDesign(side, design.id);
          if (!previewSvg) return;

          // Extract colors from the preview (which has mappings applied)
          const colorsInUse = extractColorsFromSVG(previewSvg);

          // Add each color that's not transparent
          colorsInUse.forEach(color => {
            if (color && color !== 'transparent') {
              // Find the ink color name from hex using O(1) lookup
              const inkColor =
                colorByHexMap.get(color) ||
                colorByHexMap.get(color.toLowerCase());
              if (inkColor) {
                usedColors.add(inkColor.name);
              } else if (hexToColorName[color.toUpperCase()]) {
                usedColors.add(hexToColorName[color.toUpperCase()]);
              } else {
                // If it's not a standard ink color, just show the hex
                usedColors.add(color);
              }
            }
          });
        });

        if (usedColors.size > 0) {
          colors[side] = Array.from(usedColors);
        }
      }
    });

    return colors;
  }, [
    currentView,
    sideDesigns,
    colorByHexMap,
    hexToColorName,
    getPreviewSVGForDesign,
    extractColorsFromSVG,
  ]);

  const handleStartOver = () => {
    const userConfirmed = window.confirm(
      'Are you sure you want to start over? This will clear all of your selections including products, designs, and customizations.'
    );

    if (userConfirmed) {
      // Clear all design and product data
      handleChange('design', null);
      handleChange('product', null);
      handleChange('productColor', null);
      handleChange('designCustomization', null);
      handleChange('designNotes', null);

      // Clear localStorage
      localStorage.removeItem('formData');
      localStorage.removeItem('modalStepNumber');
      localStorage.removeItem('selectedDesigns');
      localStorage.removeItem('targetDesignSide');

      // Reset to step 1
      setModalStepNumber(1);

      // Clear all local state
      setSideDesigns({ front: [], side: [], back: [] });
      setSideSvgContent({ front: {}, side: {}, back: {} });
      setSideColorMappings({ front: {}, side: {}, back: {} });
      setDesignPoolSvgContent({});
      setDesignPool([]);
      setSvgContent('');
      setDetectedColors([]);
      setColorMappings({});
      setSelectedDesignId(null);
      setDetectedBackgroundColor(null);
      setTransparentColors(new Set());
      setPngDesigns(new Set());
      setAvailableColors([]);
      setProductViews({});

      // Clear refs
      fetchedDesignsRef.current.clear();
      processedDesignsRef.current.clear();
    }
  };

  const saveStateBeforeNavigation = useCallback(() => {
    // Only save state if handleChange is available
    if (!handleChange) {
      return;
    }

    // Save current state before going to design gallery
    handleChange('designCustomization', {
      // Multi-side design data
      sideDesigns: createCleanDesignReferences(sideDesigns),
      sideColorMappings: sideColorMappings,
      positions: designPositions,
      sizes: designSizes,
      currentView: currentView,

      // Design pool - optimize to only essential data
      designPool: designPool.map(design => ({
        id: design.id,
        artCode: design.artCode,
        url: design.url || design.thumbnailUrl,
      })),

      // Keep backward compatibility for single design
      colorMappings: colorMappings,
      transparentColors: Array.from(transparentColors),
      position: designPositions.front,
      size: designSizes.front,

      // Product views for proof rendering
      productViews: productViews,

      // Don't include large SVG content in navigation saves
      // These can be refetched when needed

      // Ink colors
      inkColors:
        detectedColors &&
        detectedColors.map(color => ({
          id: color.id || color.name,
          name: color.name,
          hex_code: color.hex_code || color.hexCode,
        })),

      // PNG designs list
      pngDesigns: pngDesigns ? Array.from(pngDesigns) : [],
    });

    // Save current designs with their side assignments (without SVG content to avoid quota issues)
    const currentDesignData = [];
    Object.entries(sideDesigns).forEach(([side, designs]) => {
      if (Array.isArray(designs)) {
        designs.forEach(design => {
          if (design) {
            currentDesignData.push({
              id: design.id,
              artCode: design.artCode,
              url: design.url || design.thumbnailUrl,
              thumbnailUrl: design.thumbnailUrl || design.url,
              svgUrl: design.svgUrl || `/designs/${design.id}/svg_content`,
              assignedSide: side,
              // Note: NOT storing svgContent to avoid localStorage quota issues
            });
          }
        });
      }
    });

    // Also save designs from the design pool (unassigned designs)
    designPool.forEach(design => {
      if (design) {
        currentDesignData.push({
          id: design.id,
          artCode: design.artCode,
          url: design.url || design.thumbnailUrl,
          thumbnailUrl: design.thumbnailUrl || design.url,
          svgUrl: design.svgUrl || `/designs/${design.id}/svg_content`,
          assignedSide: 'pool', // Mark as pool design
        });
      }
    });
    try {
      if (currentDesignData.length > 0) {
        localStorage.setItem(
          'existingDesigns',
          JSON.stringify(currentDesignData)
        );
      }
    } catch (e) {
      if (e.name === 'QuotaExceededError') {
        // Try to clean up old data
        localStorage.removeItem('selectedDesigns');
        localStorage.removeItem('targetDesignSide');

        // Try again with cleaned storage
        try {
          localStorage.setItem(
            'existingDesigns',
            JSON.stringify(currentDesignData)
          );
        } catch (e2) {
          // If still failing, remove SVG content from formData to free up space
          const formData = JSON.parse(localStorage.getItem('formData') || '{}');
          if (formData.designCustomization) {
            delete formData.designCustomization.sideSvgContent;
            delete formData.designCustomization.designPoolSvgContent;
            delete formData.designCustomization.side_svg_content;
            delete formData.designCustomization.design_pool_svg_content;
            try {
              localStorage.setItem('formData', JSON.stringify(formData));
            } catch (e3) {
              // Last resort
              console.error('Unable to free up storage space');
            }
          }
          alert(
            "Storage quota exceeded. We've cleared some cached data to free up space. Your designs are still saved."
          );
        }
      }
    }

    // Save modal step to return to this page
    localStorage.setItem('modalStepNumber', modalStepNumber.toString());
    // Set a flag to indicate we're navigating to add more designs
    localStorage.setItem('addingMoreDesigns', 'true');
  }, [
    handleChange,
    sideDesigns,
    sideColorMappings,
    designPositions,
    designSizes,
    currentView,
    designPool,
    colorMappings,
    transparentColors,
    productViews,
    detectedColors,
    pngDesigns,
    modalStepNumber,
  ]);

  const handleDragStart = (e, designId, currentPosition) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);

    const container = productContainerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    // Handle both mouse and touch events
    const isTouch = e.type === 'touchstart';
    const startX = isTouch ? e.touches[0].clientX : e.clientX;
    const startY = isTouch ? e.touches[0].clientY : e.clientY;
    // Use the passed position or fall back to the state position
    const startPosX = currentPosition ? currentPosition.x : designPosition.x;
    const startPosY = currentPosition ? currentPosition.y : designPosition.y;

    // Track position during drag without triggering re-renders
    let currentX = startPosX;
    let currentY = startPosY;

    // Get the design overlay element to update transform directly
    const designOverlay = e.currentTarget.parentElement;

    const handleMove = e => {
      const clientX = isTouch ? e.touches[0].clientX : e.clientX;
      const clientY = isTouch ? e.touches[0].clientY : e.clientY;

      const deltaX = ((clientX - startX) / rect.width) * 100;
      const deltaY = ((clientY - startY) / rect.height) * 100;

      currentX = Math.max(10, Math.min(90, startPosX + deltaX));
      currentY = Math.max(10, Math.min(90, startPosY + deltaY));

      // Update position via CSS transform for smooth dragging without re-renders
      if (designOverlay) {
        designOverlay.style.left = `${currentX}%`;
        designOverlay.style.top = `${currentY}%`;
      }
    };

    const handleEnd = () => {
      setIsDragging(false);

      if (isTouch) {
        document.removeEventListener('touchmove', handleMove);
        document.removeEventListener('touchend', handleEnd);
      } else {
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleEnd);
      }

      // Don't clear active design - let user click elsewhere to deselect

      // Update React state with final position for the specific design
      const draggedDesignKey = getDesignKey(currentView, designId);
      setDesignPositions(prev => ({
        ...prev,
        [draggedDesignKey]: { x: currentX, y: currentY },
      }));

      // Save state after drag is complete
      setTimeout(() => {
        saveCustomizationState(
          colorMappings,
          transparentColors,
          {
            ...designPositions,
            [draggedDesignKey]: { x: currentX, y: currentY },
          },
          designSizes
        );
      }, 100);
    };

    if (isTouch) {
      document.addEventListener('touchmove', handleMove);
      document.addEventListener('touchend', handleEnd);
    } else {
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleEnd);
    }
  };

  const handleResizeStart = (e, handle, designId) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);

    const container = productContainerRef.current;
    if (!container) return;

    // Get the specific design's current size
    const designKey = `${currentView}-${designId}`;
    const currentDesignSize = designSizes[designKey] || {
      width: 40,
      height: 40,
    };

    const rect = container.getBoundingClientRect();
    // Handle both mouse and touch events
    const isTouch = e.type === 'touchstart';
    const startX = isTouch ? e.touches[0].clientX : e.clientX;
    const startWidth = currentDesignSize.width;
    const startHeight = currentDesignSize.height;
    const aspectRatio = startWidth / startHeight;

    // Track size during resize without triggering re-renders
    let currentWidth = startWidth;
    let currentHeight = startHeight;

    // Get the design overlay container element (the parent of the resize handle)
    const designOverlay = e.currentTarget.parentElement;

    const handleMove = e => {
      const clientX = isTouch ? e.touches[0].clientX : e.clientX;
      const deltaX = ((clientX - startX) / rect.width) * 100;

      // Handle based on which handle is being dragged
      switch (handle) {
        case 'se': // Southeast (bottom-right)
          currentWidth = Math.max(5, Math.min(80, startWidth + deltaX));
          currentHeight = currentWidth / aspectRatio; // Maintain aspect ratio
          break;
        case 'sw': // Southwest (bottom-left)
          currentWidth = Math.max(5, Math.min(80, startWidth - deltaX));
          currentHeight = currentWidth / aspectRatio;
          break;
        case 'ne': // Northeast (top-right)
          currentWidth = Math.max(5, Math.min(80, startWidth + deltaX));
          currentHeight = currentWidth / aspectRatio;
          break;
        case 'nw': // Northwest (top-left)
          currentWidth = Math.max(5, Math.min(80, startWidth - deltaX));
          currentHeight = currentWidth / aspectRatio;
          break;
        default:
          // No action needed for unhandled cases
          break;
      }

      // Update size via CSS for smooth resizing without re-renders
      // Apply scale adjustment when displaying
      if (designOverlay) {
        const scaledWidth = currentWidth / containerScale;
        const scaledHeight = currentHeight / containerScale;
        designOverlay.style.width = `${scaledWidth}%`;
        designOverlay.style.height = `${scaledHeight}%`;
      }
    };

    const handleEnd = () => {
      setIsResizing(false);

      if (isTouch) {
        document.removeEventListener('touchmove', handleMove);
        document.removeEventListener('touchend', handleEnd);
      } else {
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleEnd);
      }

      // Don't clear active design - let user click elsewhere to deselect

      // Update React state with final size for the specific design
      setDesignSizes(prev => ({
        ...prev,
        [designKey]: { width: currentWidth, height: currentHeight },
      }));

      // Save state after resize is complete
      setTimeout(() => {
        saveCustomizationState(
          colorMappings,
          transparentColors,
          designPositions,
          {
            ...designSizes,
            [designKey]: { width: currentWidth, height: currentHeight },
          }
        );
      }, 100);
    };

    if (isTouch) {
      document.addEventListener('touchmove', handleMove);
      document.addEventListener('touchend', handleEnd);
    } else {
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleEnd);
    }
  };

  const renderProductPreview = () => {
    if (!formData.product || !formData.productColor) return null;

    // Helper to clean URLs that might have extra quotes
    const cleanUrl = url => {
      if (!url) return null;
      // Remove surrounding quotes if they exist
      return typeof url === 'string' ? url.replace(/^"(.*)"$/, '$1') : url;
    };

    // Check if we have any fallback images from the order app
    const hasFallbackImages = () => {
      // Only consider marketing images as fallback for front view
      if (currentView !== 'front') {
        return false;
      }

      return !!(
        formData.productColor &&
        (cleanUrl(formData.productColor.marketingImageUrl) ||
          cleanUrl(formData.productColor.marketing_image_url))
      );
    };

    // Check if the actual view image exists (not a fallback)
    const hasActualViewImage = () => {
      if (!formData.productColor || !formData.productColor.name) return false;

      // Check if it's default color - should return false
      if (
        formData.productColor.name === 'Default' ||
        formData.productColor.id === 'default'
      ) {
        return false;
      }

      // Try exact match first
      let colorViews = productViews[formData.productColor.name];

      // If no exact match, try case-insensitive match
      if (!colorViews) {
        const colorNameLower = formData.productColor.name.toLowerCase();
        const matchingKey = Object.keys(productViews).find(
          key => key.toLowerCase() === colorNameLower
        );
        if (matchingKey) {
          colorViews = productViews[matchingKey];
        }
      }

      if (!colorViews) return false;

      switch (currentView) {
        case 'front':
          return !!colorViews.colorFrontImage;
        case 'side':
          return !!(
            colorViews.colorDirectSideImage ||
            colorViews.colorSideImage ||
            colorViews.colorLeftImage ||
            colorViews.colorRightImage
          );
        case 'back':
          return !!colorViews.colorBackImage;
        default:
          return false;
      }
    };

    // Get the current view image URL
    const getCurrentViewImage = () => {
      // Check if it's default color - should return placeholder for unavailable message
      if (
        formData.productColor &&
        (formData.productColor.name === 'Default' ||
          formData.productColor.id === 'default')
      ) {
        return 'https://via.placeholder.com/400x500/f0f0f0/999999?text=Select+a+Color';
      }

      // Check if we have S&S images for this color
      // Try exact match first
      let colorViews =
        formData.productColor && formData.productColor.name
          ? productViews[formData.productColor.name]
          : null;

      // If no exact match, try case-insensitive match
      if (!colorViews && formData.productColor && formData.productColor.name) {
        const colorNameLower = formData.productColor.name.toLowerCase();
        const matchingKey = Object.keys(productViews).find(
          key => key.toLowerCase() === colorNameLower
        );
        if (matchingKey) {
          colorViews = productViews[matchingKey];
        }
      }

      // Helper function to get fallback image
      const getFallbackImage = () => {
        // Only use marketing image as fallback for front view
        // For back and side views, return null to show the "no image" message
        if (currentView !== 'front') {
          return null;
        }

        // Priority order for fallback:
        // 1. Product color's marketing image URL (this is what's available)
        const fallbackImage =
          (formData.productColor &&
            cleanUrl(formData.productColor.marketingImageUrl)) ||
          (formData.productColor &&
            cleanUrl(formData.productColor.marketing_image_url));

        return fallbackImage;
      };

      if (colorViews) {
        switch (currentView) {
          case 'front':
            return colorViews.colorFrontImage || getFallbackImage();
          case 'side':
            // Check for various side image properties the S&S API might use
            // Prefer colorDirectSideImage over colorSideImage
            return (
              colorViews.colorDirectSideImage ||
              colorViews.colorSideImage ||
              colorViews.colorLeftImage ||
              colorViews.colorRightImage ||
              getFallbackImage()
            );
          case 'back':
            return colorViews.colorBackImage || getFallbackImage();
          default:
            return getFallbackImage();
        }
      }
      // Always fallback to available images
      return getFallbackImage();
    };

    return (
      <div className="wizard-product-preview">
        {/* View selector with design indicators */}
        <div className="view-design-selector">
          <div className="view-selector mvs">
            {['front', 'back', 'side'].map(view => {
              // Try to find color views with case-insensitive matching
              let colorViews = formData.productColor
                ? productViews[formData.productColor.name]
                : null;

              // If no exact match, try case-insensitive match
              if (
                !colorViews &&
                formData.productColor &&
                formData.productColor.name
              ) {
                const colorNameLower = formData.productColor.name.toLowerCase();
                const matchingKey = Object.keys(productViews).find(
                  key => key.toLowerCase() === colorNameLower
                );
                if (matchingKey) {
                  colorViews = productViews[matchingKey];
                }
              }

              const hasDesign =
                sideDesigns[view] && sideDesigns[view].length > 0;

              return (
                <button
                  key={view}
                  className={`button view-button ${
                    currentView === view ? 'active' : ''
                  } ${hasDesign ? 'has-design' : ''}`}
                  onClick={() => {
                    if (view !== currentView) {
                      // Start transition
                      setIsViewTransitioning(true);

                      // Small delay to allow CSS transition
                      setTimeout(() => {
                        setCurrentView(view);
                        setProductImageError(false); // Reset error state
                        // Clear active design when switching views
                        setActiveDesignId(null);
                        // Clear selection if the selected design doesn't exist on the new view
                        if (selectedDesignId && sideDesigns[view]) {
                          const designExistsOnNewView = sideDesigns[view].some(
                            d => String(d.id) === String(selectedDesignId)
                          );
                          if (!designExistsOnNewView) {
                            setSelectedDesignId(null);
                          }
                        }

                        // End transition after a short delay
                        setTimeout(() => {
                          setIsViewTransitioning(false);
                        }, 100);
                      }, 50);
                    }
                  }}
                >
                  {view.charAt(0).toUpperCase() + view.slice(1)}
                  {hasDesign && <span className="design-indicator">●</span>}
                </button>
              );
            })}
          </div>
        </div>

        <div
          className={`product-mockup-container ${
            isViewTransitioning ? 'transitioning' : ''
          } ${
            !isLoadingViews && (productImageError || !hasActualViewImage())
              ? 'image-unavailable'
              : ''
          }`}
          ref={productContainerRef}
          role="button"
          tabIndex={0}
          onClick={e => {
            // If clicking on the container background (not on a design), clear active design
            if (
              e.target === e.currentTarget ||
              e.target.classList.contains('product-base-image')
            ) {
              setActiveDesignId(null);
            }
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              if (
                e.target === e.currentTarget ||
                e.target.classList.contains('product-base-image')
              ) {
                setActiveDesignId(null);
              }
            }
          }}
        >
          {(() => {
            const imageUrl = getCurrentViewImage();
            const isImageLoaded = loadedImageUrls.has(imageUrl);
            const noActualImage = !hasActualViewImage();

            // Don't show spinner if there's no actual image for this view
            if (noActualImage && !isLoadingViews) {
              return null;
            }

            // Only show spinner when loading views or on initial load (no current image)
            return isLoadingViews || (!currentImageUrl && !isImageLoaded)
              ? !productImageError && <LoadingSpinner />
              : null;
          })()}
          {!isLoadingViews &&
            (((formData.productColor &&
              (formData.productColor.name === 'Default' ||
                formData.productColor.id === 'default')) ||
              (!hasActualViewImage() && !hasFallbackImages())) &&
              !productImageError) && (
              <ProductImageUnavailable
                colorName={formData.productColor && formData.productColor.name}
                colorId={formData.productColor && formData.productColor.id}
              />
            )}
          {(() => {
            const imageUrl = getCurrentViewImage();
            const isImageLoaded = loadedImageUrls.has(imageUrl);

            // Check if we should show the unavailable message
            const shouldShowUnavailable =
              !isLoadingViews &&
              (((formData.productColor &&
                (formData.productColor.name === 'Default' ||
                  formData.productColor.id === 'default')) ||
                (!hasActualViewImage() && !hasFallbackImages())) &&
                !productImageError);

            // Don't render the image if we're showing the unavailable message
            if (shouldShowUnavailable) {
              return null;
            }

            return (
              <img
                ref={productImageRef}
                src={imageUrl}
                alt={`${
                  formData.productColor ? formData.productColor.name : 'Product'
                } - ${currentView} view`}
                className="product-base-image"
                style={{
                  opacity:
                    isLoadingViews ||
                    (formData.productColor &&
                      (formData.productColor.name === 'Default' ||
                        formData.productColor.id === 'default'))
                      ? 0
                      : 1,
                  transition: 'opacity 0.3s ease',
                }}
                onLoad={() => {
                  setProductImageError(false);
                  setLoadedImageUrls(prev => new Set([...prev, imageUrl]));
                  // Update current image URL
                  setCurrentImageUrl(imageUrl);
                  // Recalculate scale when image loads
                  if (productContainerRef.current) {
                    const baseWidth = 450;
                    const currentWidth =
                      productContainerRef.current.offsetWidth;
                    const scale = currentWidth / baseWidth;
                    setContainerScale(scale);
                  }
                }}
                onError={() => {
                  // Don't set error if this is a placeholder or we have fallback images
                  const isPlaceholder = imageUrl.includes('placeholder');
                  const hasFallback =
                    formData.product &&
                    (formData.product.image_primary ||
                      formData.product.image_secondary ||
                      formData.product.image_interest);
                  setProductImageError(!isPlaceholder && !hasFallback);
                }}
              />
            );
          })()}
          {/* Render all designs on the current side */}
          {sideDesigns[currentView] &&
            sideDesigns[currentView].map(design => {
              const designKey = `${currentView}-${design.id}`;
              const position = designPositions[designKey] || { x: 50, y: 50 };
              const size = designSizes[designKey] || { width: 40, height: 40 };
              const svgContent =
                sideSvgContent[currentView] &&
                sideSvgContent[currentView][design.id];
              // Only show bounding box if this design is actively being manipulated
              const isActive =
                activeDesignId && String(activeDesignId) === String(design.id);
              const isSelected =
                selectedDesignId &&
                String(selectedDesignId) === String(design.id);

              // Check if this is a PNG design or large SVG design
              const isPngDesign = pngDesigns.has(String(design.id));

              // Skip if no content and not a PNG or large SVG design
              if (!svgContent && !isPngDesign) return null;

              // Apply scale adjustment for consistent sizing
              const scaledWidth = size.width / containerScale;
              const scaledHeight = size.height / containerScale;

              return (
                <div
                  key={designKey}
                  className={`design-overlay-container ${
                    (isDragging || isResizing) && isActive ? 'active' : ''
                  } ${isActive ? 'selected' : ''}`}
                  style={{
                    left: `${position.x}%`,
                    top: `${position.y}%`,
                    width: `${scaledWidth}%`,
                    height: `${scaledHeight}%`,
                    transform: 'translate(-50%, -50%)',
                    zIndex: isSelected ? 10 : 1,
                  }}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setSelectedDesignId(design.id);
                    setActiveDesignId(design.id);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      setSelectedDesignId(design.id);
                      setActiveDesignId(design.id);
                    }
                  }}
                >
                  <div
                    key={`${currentView}-${design.id}-${JSON.stringify(
                      (sideColorMappings[currentView] &&
                        sideColorMappings[currentView][design.id]) ||
                        {}
                    )}`}
                    className="design-overlay"
                    role="button"
                    tabIndex={0}
                    aria-label={`Design ${design.artCode || design.id} - ${
                      isSelected ? 'drag to move' : 'click to select'
                    }`}
                    onMouseDown={e => {
                      setSelectedDesignId(design.id);
                      setActiveDesignId(design.id);
                      // Always start drag immediately on mouse down
                      handleDragStart(e, design.id, position);
                    }}
                    onTouchStart={e => {
                      setSelectedDesignId(design.id);
                      setActiveDesignId(design.id);
                      // Always start drag immediately on touch start
                      handleDragStart(e, design.id, position);
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedDesignId(design.id);
                        setActiveDesignId(design.id);
                      }
                    }}
                  >
                    {isPngDesign ? (
                      <img
                        src={
                          design.thumbnailUrl ||
                          design.url ||
                          design.fullsizeUrl ||
                          `/designs/${design.id}/image`
                        }
                        alt={design.artCode || `Design ${design.id}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          pointerEvents: 'none',
                          userSelect: 'none',
                          WebkitUserDrag: 'none',
                        }}
                        draggable={false}
                        onLoad={e => {
                          // Calculate aspect ratio from loaded image
                          const img = e.target;
                          if (img.naturalWidth && img.naturalHeight) {
                            const aspectRatio =
                              img.naturalWidth / img.naturalHeight;
                            setDesignAspectRatios(prev => ({
                              ...prev,
                              [design.id]: aspectRatio,
                            }));
                          }
                        }}
                      />
                    ) : (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: getPreviewSVGForDesign(
                            currentView,
                            design.id
                          ),
                        }}
                        style={{
                          width: '100%',
                          height: '100%',
                        }}
                      />
                    )}
                  </div>

                  {/* Resize handles - only show for active design */}
                  {isActive && (
                    <>
                      <div
                        className="resize-handle resize-handle-nw"
                        role="button"
                        tabIndex={0}
                        aria-label="Resize from top-left"
                        onMouseDown={e => handleResizeStart(e, 'nw', design.id)}
                        onTouchStart={e =>
                          handleResizeStart(e, 'nw', design.id)
                        }
                      />
                      <div
                        className="resize-handle resize-handle-ne"
                        role="button"
                        tabIndex={0}
                        aria-label="Resize from top-right"
                        onMouseDown={e => handleResizeStart(e, 'ne', design.id)}
                        onTouchStart={e =>
                          handleResizeStart(e, 'ne', design.id)
                        }
                      />
                      <div
                        className="resize-handle resize-handle-sw"
                        role="button"
                        tabIndex={0}
                        aria-label="Resize from bottom-left"
                        onMouseDown={e => handleResizeStart(e, 'sw', design.id)}
                        onTouchStart={e =>
                          handleResizeStart(e, 'sw', design.id)
                        }
                      />
                      <div
                        className="resize-handle resize-handle-se"
                        role="button"
                        tabIndex={0}
                        aria-label="Resize from bottom-right"
                        onMouseDown={e => handleResizeStart(e, 'se', design.id)}
                        onTouchStart={e =>
                          handleResizeStart(e, 'se', design.id)
                        }
                      />
                    </>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="text-center pam">
        <p>Loading design...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="design-customizer">
        <p className="small centered mvn">1 of 2</p>
        <h2 className="c-heading c-heading-2 heading-700 centered mvn">
          Customize Your Design
        </h2>
        <div className="text-center pam">
          <p className="error-message">{error}</p>
          <button className="button mtm" onClick={handleSkip}>
            Continue Without Customizing
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="design-customizer pan">
      <div className="design-customizer-content pbl">
        <p className="small centered mvn">1 of 2</p>
        <h2 className="c-heading-2 centered mvn">The UTees Design Studio™</h2>
        <p className="mts mbm centered small">
          Preview your design proof and customize colors as needed.
        </p>
        <div className="centered mbs">
          <CustomizableDesignNotice variant="inline" />
        </div>

        <div className="customizer-layout">
          {/* Left Column - Design Assignment */}
          <div className="designs-column">
            <h3 className="column-heading">Selected Designs</h3>
            <a
              href={designGalleryUrl || '/designs'}
              className="button button--alt button--full mtm"
              onClick={e => {
                e.preventDefault();
                saveStateBeforeNavigation();
                // Navigate immediately - no need for delay
                window.location.href = designGalleryUrl || '/designs';
              }}
            >
              Add Designs
            </a>
            {/* Design Pool Section */}
            {designPool.length > 0 && (
              <div className="design-pool-section">
                <p className="small mbs">
                  Click to place designs on your product
                </p>
                <div className="design-pool-grid">
                  {designPool.map(design => (
                    <div key={design.id} className="design-pool-item">
                      <button
                        className="design-tool-remove"
                        onClick={e => {
                          e.stopPropagation();
                          handleRemoveFromPool(design.id);
                        }}
                        aria-label={`Remove ${design.artCode ||
                          `Design ${design.id}`}`}
                        title="Remove design"
                      >
                        ×
                      </button>
                      <div className="design-pool-preview">
                        {pngDesigns.has(String(design.id)) ? (
                          <img
                            src={
                              design.thumbnailUrl ||
                              design.url ||
                              design.fullsizeUrl
                            }
                            alt={design.artCode || `Design ${design.id}`}
                            className="png-preview-small"
                            onLoad={e => {
                              // Calculate aspect ratio from loaded image
                              const img = e.target;
                              if (img.naturalWidth && img.naturalHeight) {
                                const aspectRatio =
                                  img.naturalWidth / img.naturalHeight;
                                setDesignAspectRatios(prev => ({
                                  ...prev,
                                  [design.id]: aspectRatio,
                                }));
                              }
                            }}
                          />
                        ) : designPoolSvgContent[design.id] ? (
                          <div
                            className="svg-preview-small"
                            dangerouslySetInnerHTML={{
                              __html: designPoolSvgContent[design.id],
                            }}
                          />
                        ) : imageLoadErrors.has(design.id) ? (
                          <LoadingSpinner />
                        ) : design.thumbnailUrl || design.url ? (
                          <>
                            {imageLoadingStates.get(design.id) !== 'loaded' && (
                              <LoadingSpinner />
                            )}
                            <img
                              src={design.thumbnailUrl || design.url}
                              alt={design.artCode || `Design ${design.id}`}
                              className="design-thumbnail"
                              style={{
                                display:
                                  imageLoadingStates.get(design.id) === 'loaded'
                                    ? 'block'
                                    : 'none',
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                zIndex: 2,
                              }}
                              onLoad={() => {
                                setImageLoadingStates(prev =>
                                  new Map(prev).set(design.id, 'loaded')
                                );
                              }}
                              onError={() => {
                                setImageLoadErrors(
                                  prev => new Set([...prev, design.id])
                                );
                              }}
                            />
                          </>
                        ) : (
                          <LoadingSpinner />
                        )}
                      </div>
                      <div className="design-pool-info">
                        <span className="design-code">
                          {design.artCode || `Design ${design.id}`}
                        </span>
                        <div className="design-pool-actions">
                          <button
                            className="tiny button pxs pys man"
                            onClick={() =>
                              handleAddDesignFromPoolToSide(design.id, 'front')
                            }
                            disabled={
                              Array.isArray(sideDesigns.front) &&
                              sideDesigns.front.some(
                                d => String(d.id) === String(design.id)
                              )
                            }
                          >
                            {Array.isArray(sideDesigns.front) &&
                            sideDesigns.front.some(
                              d => String(d.id) === String(design.id)
                            )
                              ? 'On Front'
                              : 'Add to Front'}
                          </button>
                          <button
                            className="tiny button pxs pys man"
                            onClick={() =>
                              handleAddDesignFromPoolToSide(design.id, 'back')
                            }
                            disabled={
                              Array.isArray(sideDesigns.back) &&
                              sideDesigns.back.some(
                                d => String(d.id) === String(design.id)
                              )
                            }
                          >
                            {Array.isArray(sideDesigns.back) &&
                            sideDesigns.back.some(
                              d => String(d.id) === String(design.id)
                            )
                              ? 'On Back'
                              : 'Add to Back'}
                          </button>
                          <button
                            className="tiny button pxs pys man"
                            onClick={() =>
                              handleAddDesignFromPoolToSide(design.id, 'side')
                            }
                            disabled={
                              Array.isArray(sideDesigns.side) &&
                              sideDesigns.side.some(
                                d => String(d.id) === String(design.id)
                              )
                            }
                          >
                            {Array.isArray(sideDesigns.side) &&
                            sideDesigns.side.some(
                              d => String(d.id) === String(design.id)
                            )
                              ? 'On Side'
                              : 'Add to Side'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="design-placement-container">
              <h3 className="column-heading mbs">Design Placement</h3>
              {/* Design Placement Cards */}
              <div className="design-placement-section">
                {['front', 'back', 'side'].map(side => {
                  const designs = sideDesigns[side] || [];

                  return (
                    <div key={side} className="placement-card">
                      <h4 className="placement-label man pls pts">
                        {side.charAt(0).toUpperCase() + side.slice(1)}
                      </h4>
                      {designs.length > 0 ? (
                        <div className="placement-content has-designs">
                          {designs.map(design => (
                            <div
                              key={design.id}
                              className="assigned-design-item"
                            >
                              <div className="assigned-design-preview">
                                {(() => {
                                  const svgContent =
                                    sideSvgContent[side] &&
                                    sideSvgContent[side][design.id];
                                  if (svgContent) {
                                    return (
                                      <div
                                        className="svg-preview-small"
                                        dangerouslySetInnerHTML={{
                                          __html: getPreviewSVGForDesign(
                                            side,
                                            design.id
                                          ),
                                        }}
                                      />
                                    );
                                  } else if (imageLoadErrors.has(design.id)) {
                                    return <LoadingSpinner />;
                                  } else if (
                                    design.thumbnailUrl ||
                                    design.url
                                  ) {
                                    return (
                                      <img
                                        src={design.thumbnailUrl || design.url}
                                        alt={
                                          design.artCode ||
                                          `Design ${design.id}`
                                        }
                                        className="design-thumbnail"
                                        onError={() => {
                                          setImageLoadErrors(
                                            prev =>
                                              new Set([...prev, design.id])
                                          );
                                        }}
                                        onLoad={e => {
                                          // Calculate aspect ratio from loaded image
                                          const img = e.target;
                                          if (
                                            img.naturalWidth &&
                                            img.naturalHeight
                                          ) {
                                            const aspectRatio =
                                              img.naturalWidth /
                                              img.naturalHeight;
                                            setDesignAspectRatios(prev => ({
                                              ...prev,
                                              [design.id]: aspectRatio,
                                            }));
                                          }
                                        }}
                                      />
                                    );
                                  } else {
                                    return <LoadingSpinner />;
                                  }
                                })()}
                              </div>
                              <div className="assigned-design-info">
                                <span className="design-code">
                                  {design.artCode || `Design ${design.id}`}
                                </span>
                                <div className="design-actions">
                                  {/* Quick move buttons */}
                                  <div className="quick-move-buttons">
                                    {side !== 'front' && (
                                      <button
                                        className="move-btn"
                                        onClick={() =>
                                          handleMoveDesign(design.id, 'front')
                                        }
                                        title={
                                          sideDesigns.front
                                            ? 'Swap with front design'
                                            : 'Move to front'
                                        }
                                      >
                                        {sideDesigns.front ? '⇄' : '→'} Front
                                      </button>
                                    )}
                                    {side !== 'back' &&
                                      (() => {
                                        let colorViews =
                                          formData.productColor &&
                                          productViews[
                                            formData.productColor.name
                                          ];
                                        if (
                                          !colorViews &&
                                          formData.productColor &&
                                          formData.productColor.name
                                        ) {
                                          const colorNameLower = formData.productColor.name.toLowerCase();
                                          const matchingKey = Object.keys(
                                            productViews
                                          ).find(
                                            key =>
                                              key.toLowerCase() ===
                                              colorNameLower
                                          );
                                          if (matchingKey) {
                                            colorViews =
                                              productViews[matchingKey];
                                          }
                                        }
                                        const backAvailable =
                                          colorViews &&
                                          colorViews.colorBackImage;

                                        return backAvailable ? (
                                          <button
                                            className="move-btn"
                                            onClick={() =>
                                              handleMoveDesign(
                                                design.id,
                                                'back'
                                              )
                                            }
                                            title={
                                              sideDesigns.back
                                                ? 'Swap with back design'
                                                : 'Move to back'
                                            }
                                          >
                                            {sideDesigns.back ? '⇄' : '→'} Back
                                          </button>
                                        ) : null;
                                      })()}
                                    {side !== 'side' &&
                                      (() => {
                                        // Check if side view is available
                                        if (!formData.productColor) {
                                          return null;
                                        }

                                        let colorViews =
                                          productViews[
                                            formData.productColor.name
                                          ];
                                        if (
                                          !colorViews &&
                                          formData.productColor.name
                                        ) {
                                          const colorNameLower = formData.productColor.name.toLowerCase();
                                          const matchingKey = Object.keys(
                                            productViews
                                          ).find(
                                            key =>
                                              key.toLowerCase() ===
                                              colorNameLower
                                          );
                                          if (matchingKey) {
                                            colorViews =
                                              productViews[matchingKey];
                                          }
                                        }
                                        const sideAvailable =
                                          colorViews &&
                                          (colorViews.colorDirectSideImage ||
                                            colorViews.colorSideImage);

                                        return sideAvailable ? (
                                          <button
                                            className="move-btn"
                                            onClick={() =>
                                              handleMoveDesign(
                                                design.id,
                                                'side'
                                              )
                                            }
                                            title={
                                              sideDesigns.side
                                                ? 'Swap with side design'
                                                : 'Move to side'
                                            }
                                          >
                                            {sideDesigns.side ? '⇄' : '→'} Side
                                          </button>
                                        ) : null;
                                      })()}
                                  </div>
                                  <button
                                    className="design-tool-remove"
                                    onClick={() =>
                                      handleRemoveDesignFromSide(
                                        side,
                                        design.id
                                      )
                                    }
                                    aria-label={`Remove ${design.artCode ||
                                      `Design ${design.id}`} from ${side}`}
                                    title="Remove from this side"
                                  >
                                    ×
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <a
                          className="placement-content empty"
                          href={designGalleryUrl}
                          onClick={e => {
                            e.preventDefault();
                            // Save the target side to localStorage so the gallery knows where to add the design
                            localStorage.setItem('targetDesignSide', side);
                            saveStateBeforeNavigation();
                            // Navigate immediately - no need for delay
                            window.location.href = designGalleryUrl;
                          }}
                          style={{
                            textDecoration: 'none',
                            color: 'inherit',
                            display: 'block',
                          }}
                        >
                          <div className="empty-slot">
                            <div className="button button--alt mtm">
                              Add Design
                            </div>
                            <p className="empty-text">Browse Design Gallery</p>
                          </div>
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Center Column - Product Preview */}
          <div className="preview-column">
            <div className="preview-container">
              <h3 className="column-heading">Design Proof</h3>
              {formData.product ? (
                <>
                  {renderProductPreview()}

                  {/* Design Position & Size Controls - beneath the proof */}
                  {sideDesigns[currentView] &&
                    sideDesigns[currentView].length > 0 &&
                    selectedDesignId && (
                      <div className="design-controls">
                        <p className="control-hint">
                          {sideDesigns[currentView] &&
                          sideDesigns[currentView].length > 1
                            ? `Select a design to edit • ${
                                selectedDesignId
                                  ? 'Drag to move | Drag corners to resize'
                                  : 'Click on a design above'
                              }`
                            : 'Drag to move • Drag corners to resize'}
                        </p>
                        <div className="control-label">
                          <span>Size</span>
                          <input
                            type="range"
                            min="5"
                            max="80"
                            step="1"
                            value={designSize.width}
                            onChange={e => {
                              const newWidth = parseFloat(e.target.value);
                              const aspectRatio =
                                designSize.width / designSize.height;
                              const newHeight = newWidth / aspectRatio;
                              setDesignSize({
                                width: newWidth,
                                height: newHeight,
                              });
                            }}
                            className="size-slider"
                          />
                          <span className="size-value">
                            {Math.round(designSize.width)}%
                          </span>
                        </div>
                      </div>
                    )}
                </>
              ) : (
                <div className="no-product-message">
                  <div className="text-center">
                    <p className="mbxl">
                      Select a product to preview your customized design
                    </p>
                    <a
                      href="/products"
                      className="button button--alt"
                      onClick={e => {
                        e.preventDefault();
                        saveStateBeforeNavigation();
                        // Navigate immediately - no need for delay
                        window.location.href = '/products';
                      }}
                    >
                      Browse Products
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Color Editor */}
          <div className="editor-column">
            <div className="editor-container">
              <h3 className="column-heading mbm">Customizations</h3>
              {/* Product Info - moved from center column */}
              {formData.product && formData.productColor ? (
                <div className="product-info-section">
                  <button
                    className="design-tool-remove"
                    onClick={() => handleRemoveProduct()}
                    aria-label="Remove product"
                    title="Remove product"
                  >
                    ×
                  </button>
                  <div>
                    <p className="product-name mvn">
                      {formData.product.brandName} {formData.product.style} -{' '}
                      {formData.productColor.name}
                    </p>
                    <small>{formData.product.name}</small>
                  </div>
                  <div className="mtm">
                    <button
                      href="/products"
                      className="button-small button--alt"
                      onClick={e => {
                        e.preventDefault();
                        saveStateBeforeNavigation();
                        // Navigate immediately - no need for delay
                        window.location.href = '/products';
                      }}
                    >
                      Change Product
                    </button>
                  </div>
                </div>
              ) : (
                <div className="product-info-section no-product">
                  <p className="empty-state-text mvn">No product selected</p>
                  <a
                    href="/products"
                    className="button button--alt button--full"
                    onClick={e => {
                      e.preventDefault();
                      saveStateBeforeNavigation();
                      // Small delay to ensure state is saved
                      setTimeout(() => {
                        window.location.href = '/products';
                      }, 100);
                    }}
                  >
                    Browse Products
                  </a>
                </div>
              )}

              <div className="color-editor">
                {/* Product Color Selection */}
                {availableColors.length > 0 && (
                  <details open={!formData.productColor || (formData.productColor && formData.productColor.name === 'Default')} className="mbs">
                    <summary className="dark-filter-heading accordion-header">
                      Edit Product Color
                    </summary>
                    <div className="accordion-content">
                      {isLoadingColors && (
                        <p className="loading-message">
                          Loading color options...
                        </p>
                      )}
                      {!isLoadingColors && availableColors.length === 1 && (
                        <p className="single-color-message">
                          This product is only available in{' '}
                          {availableColors[0].name}
                        </p>
                      )}
                      {!isLoadingColors && availableColors.length > 1 && (
                        <div className="color-options compact">
                          {availableColors.map(color => (
                            <div
                              key={color.id}
                              className={`color-option ${
                                formData.productColor &&
                                String(formData.productColor.id) ===
                                  String(color.id)
                                  ? 'active'
                                  : ''
                              }`}
                              onClick={() => handleColorSwitch(color)}
                              onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleColorSwitch(color);
                                }
                              }}
                              role="button"
                              tabIndex={0}
                              title={`${color.name} - ${color.hexCode ||
                                'No hex code'}`}
                              aria-label={`Select ${color.name} color`}
                            >
                              <div
                                className="color-circle"
                                style={{
                                  backgroundColor: color.hexCode
                                    ? `#${color.hexCode}`
                                    : '#ccc',
                                  backgroundImage: !color.hexCode
                                    ? 'linear-gradient(45deg, #ddd 25%, transparent 25%, transparent 75%, #ddd 75%), linear-gradient(45deg, #ddd 25%, transparent 25%, transparent 75%, #ddd 75%)'
                                    : 'none',
                                  backgroundSize: '10px 10px',
                                  backgroundPosition: '0 0, 5px 5px',
                                }}
                              />
                              <span className="color-name">{color.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </details>
                )}

                {/* Design Colors Section - only show if current side has designs and one is selected */}
                {sideDesigns[currentView] &&
                sideDesigns[currentView].length > 0 &&
                selectedDesignId ? (
                  <details open className="mts">
                    <summary className="dark-filter-heading accordion-header">
                      Edit Design Colors ({currentView})
                    </summary>
                    <div className="accordion-content">
                      {/* Check if current selected design is PNG */}
                      {selectedDesignId &&
                        pngDesigns.has(String(selectedDesignId)) && (
                          <div className="png-design-notice">
                            <div className="notice-content">
                              <p className="notice-text">
                                <strong>Note:</strong> This design can't be
                                edited in real-time due to the file type.
                              </p>
                              <p className="notice-subtext">
                                Let us know what changes you'd like in the
                                Design Notes below and our artists can customize
                                it for you.
                              </p>
                            </div>
                          </div>
                        )}
                      <div className="color-editor-buttons">
                        <button
                          className="button dc-button--secondary button--full mtm mbs"
                          onClick={resetDesignColors}
                          disabled={
                            pngDesigns.has(String(selectedDesignId)) ||
                            (Object.keys(colorMappings).every(
                              color => colorMappings[color] === color
                            ) &&
                              transparentColors.size === 0)
                          }
                        >
                          Reset Design Colors
                        </button>
                      </div>
                      {/* Only show color controls if not a PNG */}
                      {!pngDesigns.has(String(selectedDesignId)) && (
                        <div className="detected-colors">
                          {detectedColors
                            .filter(color => {
                              // Filter out the background color since it's automatically removed
                              return color !== detectedBackgroundColor;
                            })
                            .map(color => (
                              <div
                                key={color}
                                className={`color-item ${
                                  selectedColor === color ? 'selected' : ''
                                } ${
                                  transparentColors.has(color)
                                    ? 'transparent'
                                    : ''
                                }`}
                                role="button"
                                tabIndex={0}
                                onClick={() => setSelectedColor(color)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setSelectedColor(color);
                                  }
                                }}
                                title={`${getColorDisplay(color)}${
                                  getColorDisplay(color) !== color
                                    ? ` (${color})`
                                    : ''
                                }`}
                              >
                                <div
                                  className="color-swatch"
                                  style={{
                                    backgroundColor: transparentColors.has(
                                      color
                                    )
                                      ? 'transparent'
                                      : colorMappings[color] || color,
                                  }}
                                />
                                <span className="color-label">
                                  {colorMappings[color] &&
                                  colorMappings[color] !== color ? (
                                    <>
                                      <span
                                        style={{
                                          textDecoration: 'line-through',
                                          opacity: 0.6,
                                        }}
                                      >
                                        {getColorDisplay(color)}
                                      </span>
                                      {' → '}
                                      {getColorDisplay(colorMappings[color])}
                                    </>
                                  ) : (
                                    getColorDisplay(color)
                                  )}
                                </span>
                                <button
                                  className="dc-button--transparent"
                                  onClick={e => {
                                    e.stopPropagation();
                                    toggleTransparent(color);
                                  }}
                                  title={
                                    transparentColors.has(color)
                                      ? 'Restore color'
                                      : 'Make transparent'
                                  }
                                >
                                  {transparentColors.has(color) ? '↩' : '🚫'}
                                </button>
                              </div>
                            ))}
                        </div>
                      )}

                      {/* Show colors from other sides */}
                      {(() => {
                        // otherSideColors is already computed via useMemo above
                        const hasSideColors =
                          Object.keys(otherSideColors).length > 0;

                        if (hasSideColors) {
                          return (
                            <div className="other-side-colors">
                              <p className="other-side-colors-label">
                                Colors used on other sides:
                                <span className="help-text">
                                  {' '}
                                  (click to apply)
                                </span>
                              </p>
                              {Object.entries(otherSideColors).map(
                                ([side, colors]) => (
                                  <div
                                    key={side}
                                    className="other-side-color-row"
                                  >
                                    <span className="side-label">
                                      {side.charAt(0).toUpperCase() +
                                        side.slice(1)}
                                      :
                                    </span>
                                    <div className="side-colors">
                                      {colors.map(colorName => {
                                        const inkColor = standardInkColors.find(
                                          c => c.name === colorName
                                        );
                                        const isHexColor = colorName.startsWith(
                                          '#'
                                        );
                                        const backgroundColor = inkColor
                                          ? inkColor.hex
                                          : isHexColor
                                          ? colorName
                                          : '#ccc';
                                        const displayName = inkColor
                                          ? colorName
                                          : isHexColor
                                          ? getColorDisplay(colorName)
                                          : colorName;

                                        return (
                                          <div
                                            key={colorName}
                                            className="side-color-chip clickable"
                                            title={`Click to use ${displayName}`}
                                            onClick={() => {
                                              if (selectedColor) {
                                                const colorToApply = inkColor
                                                  ? inkColor.hex
                                                  : isHexColor
                                                  ? colorName
                                                  : null;
                                                if (colorToApply) {
                                                  updateColorMapping(
                                                    selectedColor,
                                                    colorToApply
                                                  );
                                                }
                                              } else {
                                                alert(
                                                  'Please select a color in your design to change first'
                                                );
                                              }
                                            }}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={e => {
                                              if (
                                                (e.key === 'Enter' ||
                                                  e.key === ' ') &&
                                                selectedColor
                                              ) {
                                                e.preventDefault();
                                                const colorToApply = inkColor
                                                  ? inkColor.hex
                                                  : isHexColor
                                                  ? colorName
                                                  : null;
                                                if (colorToApply) {
                                                  updateColorMapping(
                                                    selectedColor,
                                                    colorToApply
                                                  );
                                                }
                                              }
                                            }}
                                          >
                                            <div
                                              className="side-color-swatch"
                                              style={{
                                                backgroundColor: backgroundColor,
                                              }}
                                            />
                                            <span className="side-color-name">
                                              {displayName}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )
                              )}
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {/* Only show color replacement for customizable SVG designs */}
                      {!pngDesigns.has(String(selectedDesignId)) && (
                        <div className="color-replacement-section">
                          <h4 className="mts mbs">
                            {selectedColor ? (
                              <>
                                Replace{' '}
                                <span className="color-with-swatch">
                                  <span
                                    className="inline-color-swatch"
                                    style={{
                                      backgroundColor: selectedColor,
                                      display: 'inline-block',
                                      width: '16px',
                                      height: '16px',
                                      borderRadius: '3px',
                                      verticalAlign: 'middle',
                                      marginRight: '4px',
                                      border: '1px solid rgba(0,0,0,0.2)',
                                    }}
                                  />
                                  {getColorDisplay(selectedColor)}
                                </span>{' '}
                                with:
                              </>
                            ) : (
                              <>
                                Select a design color above, then choose a
                                replacement color
                              </>
                            )}
                          </h4>
                          <div className="standard-colors-grid">
                            {standardInkColors.map(inkColor => (
                              <div
                                key={inkColor.hex}
                                className={`ink-color-option ${
                                  colorMappings[selectedColor] === inkColor.hex
                                    ? 'selected'
                                    : ''
                                }`}
                                role="button"
                                tabIndex={0}
                                onClick={() =>
                                  selectedColor &&
                                  updateColorMapping(
                                    selectedColor,
                                    inkColor.hex
                                  )
                                }
                                onKeyDown={e => {
                                  if (
                                    selectedColor &&
                                    (e.key === 'Enter' || e.key === ' ')
                                  ) {
                                    e.preventDefault();
                                    updateColorMapping(
                                      selectedColor,
                                      inkColor.hex
                                    );
                                  }
                                }}
                                title={
                                  selectedColor
                                    ? inkColor.name
                                    : 'Select a design color first'
                                }
                                style={{
                                  opacity: selectedColor ? 1 : 0.5,
                                  cursor: selectedColor
                                    ? 'pointer'
                                    : 'not-allowed',
                                }}
                              >
                                <div
                                  className="ink-color-swatch"
                                  style={{ backgroundColor: inkColor.hex }}
                                />
                                <span className="ink-color-name">
                                  {inkColor.name}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </details>
                ) : (
                  /* Show message when no designs are added */
                  <div className="no-designs-message mts">
                    <div className="empty-state-box">
                      <p className="empty-state-text">
                        Please add a design to your product to begin
                        customizing.
                      </p>
                      <p className="empty-state-subtext mts">
                        Select designs from the "Selected Designs" section on
                        the left, or browse more designs to add to your
                        collection.
                      </p>
                    </div>
                  </div>
                )}
              </div>
              <div className="design-notes-section mtm">
                <label className="input-label-thin" htmlFor="designNotes">
                  Design Notes
                </label>
                <textarea
                  id="designNotes"
                  value={formData.designNotes || ''}
                  className="field"
                  onChange={e => {
                    handleChange('designNotes', e.target.value);
                  }}
                  placeholder="Include design details, print locations, or special instructions here! Feel free to reference other designs and products."
                  rows="3"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="design-customizer-footer">
        <div className="row row-pushed row-centered man">
          {modalStepNumber === 1 ? (
            <div>
              <button
                className="small button-pseudo--alert"
                onClick={handleStartOver}
              >
                Start Over
              </button>
            </div>
          ) : (
            <div>
              <button className="button button--alt mbn" onClick={handleBack}>
                Back
              </button>
            </div>
          )}
          <div>
            <button className="button-pseudo small mrs" onClick={handleSkip}>
              Skip Customization
            </button>
            <button className="button mbn" onClick={handleNext}>
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Design Picker Modal */}
    </div>
  );
};

export default DesignCustomizer;
